import { isEmpty } from "lodash";
import isOnlineStatus from "is-online";
import { defer, forkJoin, from, Observable, of, pipe, zip } from "rxjs";
import { catchError, finalize, map, mergeMap, switchMap, switchMapTo, tap, toArray } from "rxjs/operators";

import { HttpEvent, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { FormatDate } from "../../_objects/format-date";
import { Inspection, InspectionForOffline, InspectionImagesCount, InspectionPermission } from "../../_objects/inspection";
import {
  ExifWrapperResult,
  ImagesEvaluationList,
  InspectionImage,
  InspectionImageCreate,
  InspectionImageForOffline,
  InspectionImageListItem,
  InspectionImageMultipleUpdate,
  InspectionImageOrder,
  InspectionImageOrderChange,
  InspectionImageUpdate,
  MeasurementData,
  MeasurementInfo,
  Metadata,
  ModifiedImageUseUpdate,
  ReadImageOption,
  UploadOption
} from "../../_objects/inspection-image";
import { InspectionImageFilters } from "../../_objects/inspection-image-filters";
import { InspectionReport, ReportLocation } from "../../_objects/inspection-report";
import { Mark, MarkItem } from "../../_objects/mark";
import { AnyImageMetadata } from "../../_objects/metadata/any-image-metadata";
import { BrianImageMetadata } from "../../_objects/metadata/brian-image-metadata";
import { Iv7ImageMetadata, Iv7ImageMetadataDef } from "../../_objects/metadata/iv7-image-metadata";
import { SoleilImageMetadata, SoleilImageMetadataDef } from "../../_objects/metadata/soleil-image-metadata";
import { TheseusImageMetadata } from "../../_objects/metadata/theseus-image-metadata";
import { VegaImageMetadata } from "../../_objects/metadata/vega-image-metadata";
import { VideoMetadata } from "../../_objects/metadata/video-metadata";
import { Vertices2Df } from "../../_objects/remeasurement";
import { SubjectLocation, SubjectModel } from "../../_objects/subject-model";
import { TenantDataFile } from "../../_objects/tenant-data";
import { FILE_SYSTEM } from "../../_offline/electron.constants";
import { IndividualReportSchema } from "../../_offline/models/individual-report.schema";
import { InspectionSchema, InspectionSchemaStatusUpdate, InspectionSchemaUpdate } from "../../_offline/models/inspection.schema";
import { InspectionImageBaseSchema, InspectionImageSchema, InspectionImageSchemaUpdate } from "../../_offline/models/inspectionImage.schema";
import { SubjectModelSchema } from "../../_offline/models/subjectModel.schema";
import { UploadDataType, UploadManagementSchema } from "../../_offline/models/upload-management.schema";
import { OfflineUtilsService } from "../../_offline/services/offline-utils.service";
import {
  GUID,
  IMAGE_FILETYPE,
  INSPECTION_IMAGE_STATUS_CODE,
  INSPECTION_REPORT_STATUS_CODE,
  INSPECTION_STATUS_CODE,
  MEASUREMENT_IMAGE_TYPE,
  SCOPE_MODEL,
  TAGS_FLAG_VALUE,
  TenantConfig,
  USE_IMAGE_TYPE
} from "../../app.constants";
import { UtilsService } from "../_utils.service";
import { IndividualReportDbService } from "../database/individual-report-db.service";
import { InspectionDbService } from "../database/inspection-db.service";
import { InspectionImageDbService } from "../database/inspection-image-db.service";
import { UploadManagementDbService } from "../database/upload-management-db.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { TenantConfigService } from "../http-api/tenant-config.service";
import { InspectionImageContractService } from "../interfaces/inspection-image-contract.service";
import { NetworkStatusService } from "../network-status.service";
import { OfflineManagementService } from "../offline-management.service";
import { InspectionImageOnlineService } from "../online/inspection-image-online.service";
import { InspectionReportOnlineService } from "../online/inspection-report-online.service";
import { InspectionOfflineService } from "./inspection-offline.service";
import { LoginUserOfflineService } from "./login-user-offline.service";
import { MarkOfflineService } from "./mark-offline.service";
import { StereoMeasurementImageOfflineService } from "./stereo-measurement-offline.service";
import { SubjectModelOfflineService } from "./subject-model-offline.service";
import { ImagesFiltersStateService } from "../filter-state.service";
import { MviqUtilsService } from "../mviq-utils.service";
import { VideoUtilsService } from "../video-utils.service";
import { SubjectLocationOfflineService } from "./subject-location-offline.service";
import { MeasurementMetaDataService } from "./measurement-metadata.service";
import { MerryImageMetadata, MerryUploadMetadata } from "./merry-image-metadata";
import { MerryMeasurable } from "./merry-measurable";
import { MerryUploadAttributes, MerryUploadService } from "./merry-upload.service";
import { ExifReaderService } from "../exif-reader.service";

@Injectable({
  providedIn: "root"
})
export class InspectionImageOfflineService implements InspectionImageContractService {
  readonly MODIFIED_PREFIX = "modified_";
  readonly REMEASURED_PREFIX = "measurement_";

  private readonly requiredImageConversionExtensions: Array<string> = [
    IMAGE_FILETYPE.EXTENSION_HEIC,
    IMAGE_FILETYPE.EXTENSION_HEIF,
    IMAGE_FILETYPE.EXTENSION_TIF,
    IMAGE_FILETYPE.EXTENSION_TIFF,
    IMAGE_FILETYPE.EXTENSION_AVI
  ];

  private getConversionExtension(extension: string) {
    if (extension.toLowerCase() == IMAGE_FILETYPE.EXTENSION_AVI.toLowerCase()) {
      return IMAGE_FILETYPE.EXTENSION_MP4;
    }
    return IMAGE_FILETYPE.EXTENSION_JPEG;
  }

  private readonly requiredImageConversionExtensionPattern: RegExp = new RegExp(
    "\\.(" + this.requiredImageConversionExtensions.join("|") + ")$",
    "i"
  );

  private readonly requiredAviConversionExtensions: Array<string> = [IMAGE_FILETYPE.EXTENSION_AVI];

  private readonly requiredAviConversionExtensionPattern: RegExp = new RegExp("\\.(" + this.requiredAviConversionExtensions.join("|") + ")$", "i");

  get user() {
    return this.loginUserService.loginUser;
  }

  get userId() {
    return this.user?.id;
  }

  get tenantId() {
    return this.loginUserService.tenantId;
  }

  get defaultMark() {
    const tenantConfig = this.tenantConfigService.tenantConfig;
    return tenantConfig.find((_) => _.key === TenantConfig.DefaultMarkWhenUploadingNoMarkImage.KEY).value as number;
  }

  private get isAppOnline() {
    return this.networkStatusService.isOnline;
  }

  constructor(
    private inspectionDbService: InspectionDbService,
    private inspectionImageDbService: InspectionImageDbService,
    private inspectionOfflineService: InspectionOfflineService,
    private subjectModelOfflineService: SubjectModelOfflineService,
    private individualReportDbService: IndividualReportDbService,
    private filterStateService: ImagesFiltersStateService,
    private markOfflineService: MarkOfflineService,
    private loginUserService: LoginUserOfflineService,
    private uploadManagementDbService: UploadManagementDbService,
    private utils: UtilsService,
    private tenantConfigService: TenantConfigService,
    private fileSysAccessService: FileSystemAccessService,
    private networkStatusService: NetworkStatusService,
    private offlineManagementService: OfflineManagementService,
    private offlineUtilsService: OfflineUtilsService,
    private inspectionImageOnlineService: InspectionImageOnlineService,
    private inspectionReportOnlineService: InspectionReportOnlineService,
    private stereoMeasurementImageOfflineService: StereoMeasurementImageOfflineService,
    private subjectLocationOfflineService: SubjectLocationOfflineService,
    private mviqUtilsService: MviqUtilsService,
    private videoUtilsService: VideoUtilsService,
    private exifReaderService: ExifReaderService,
    private merryUploadService: MerryUploadService
  ) {}

  updateInspectionImageFile(inspectionId: string, inspectionImageId: string, file: File): Observable<void> {
    // The original binary was rewritten in place (re-measurement metadata embedded in its XMP),
    // so register it for the next push sync. The image info record is reset together with the
    // file record: the push sync only receives the blob upload URL for the original binary
    // while uploading the image info (PushOfflineDataSyncService.postOfflineInspectionImage).
    return forkJoin([
      this.uploadManagementDbService.get(this.tenantId, this.userId, inspectionImageId, UploadDataType.InspectionImageFile),
      this.uploadManagementDbService.get(this.tenantId, this.userId, inspectionImageId, UploadDataType.InspectionImageInfo)
    ]).pipe(
      mergeMap(([fileRecord, infoRecord]) => {
        const records = [
          fileRecord ??
            new UploadManagementSchema(inspectionImageId, this.tenantId, UploadDataType.InspectionImageFile, false, this.userId, inspectionId),
          infoRecord ??
            new UploadManagementSchema(inspectionImageId, this.tenantId, UploadDataType.InspectionImageInfo, false, this.userId, inspectionId)
        ];
        records.forEach((record) => (record.isUploaded = false));
        return forkJoin(records.map((record) => this.uploadManagementDbService.put(record)));
      }),
      map(() => void 0)
    );
  }

  // IM01 GET
  getInspectionImages(inspectionId: string, filters: InspectionImageFilters): Observable<InspectionImageListItem[]> {
    let blobPath: string = "";

    return defer(() => this.fileSysAccessService.getSaveBlobFolderPath()).pipe(
      mergeMap((path) => {
        blobPath = path;
        return forkJoin({
          images: this.inspectionImageDbService.getAll(inspectionId),
          inspection: this.inspectionDbService.getInspection(inspectionId, true)
        });
      }),
      this.clearStaleImageLocations(),
      mergeMap((results) => {
        let imageSchemas: Array<InspectionImageSchema> = results.filter(
          (schema) =>
            !this.filterStateService.inspectionImagesLocationFilters ||
            this.filterStateService.inspectionImagesLocationFilters.length < 1 ||
            this.filterStateService.inspectionImagesLocationFilters.some((l) => l === schema.subjectLocationId)
        );

        // mark指定がある場合フィルタリングする
        if (!isEmpty(filters?.marks)) {
          imageSchemas = imageSchemas.filter((schema) => {
            const metadata = JSON.parse(schema.metadata) as Metadata;
            return filters.marks.some((m) => m === metadata.mark);
          });
        }

        return imageSchemas;
      }),
      mergeMap((value) => {
        const isVideo = value.mimeType?.startsWith("video/");
        const videoFileUrl = FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(value.localFilePath, value.usedImageMode);
        const durationOb = isVideo ? this.videoUtilsService.getLocalVideoDuration(videoFileUrl) : of(null);
        return forkJoin({
          image: of(value),
          exists: this.fileSysAccessService.isFileExists(this.genFilePath(value.thumbFilePath, value.usedImageMode)),
          duration: durationOb
        });
      }),
      mergeMap(({ image, exists, duration }) => {
        const schema: InspectionImageSchema = image;
        const isThumbnailFileExists: boolean = exists;
        const videoDuration: number | null = duration;

        const result: InspectionImageListItem = new InspectionImageListItem();
        result.id = schema.id;
        result.inspectionId = schema.inspectionId;
        result.subjectLocationId = schema.subjectLocationId;
        result.mimeType = schema.mimeType;
        result.datetime = schema.datetime; // 画像の撮影日
        result.fileName = schema.fileName;

        if (isThumbnailFileExists) {
          result.thumbFilePath =
            FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.thumbFilePath, schema.usedImageMode) + this.genCacheParam();
        } else {
          // サムネイル処理が無かった場合を考慮。
          result.thumbFilePath =
            FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.localFilePath, schema.usedImageMode) + this.genCacheParam();
        }
        result.permission = this.getPermission(schema);
        result.sortOrder = schema.sortOrder;
        result.status = schema.status;

        const metadata = JSON.parse(schema.metadata) as Metadata;
        result.imageDescription = schema.imageDescription;
        result.mark = metadata.mark;
        result.title = metadata.title;
        result.tags = metadata.tags;
        result.comment = schema.comment;
        result.orientation = schema.orientation;
        result.isDataPlate = schema.isDataPlate;
        result.usedImageMode = schema.usedImageMode;
        result.hasModifiedImage = schema.modifiedItems !== null;
        result.hasMeasurementImage = schema.measurementItems !== null;
        result.isMeasurementEnabled = schema.isMeasurementEnabled;
        result.videoDuration = videoDuration;

        return of(result);
      }),
      toArray(),
      mergeMap((inspectionImageListItems) => {
        return of(inspectionImageListItems.sort((img1, img2) => this.orderSort(img1, img2)));
      })
    );
  }

  private clearStaleImageLocations() {
    return pipe(
      switchMap(({ images, inspection }: { images: InspectionImageSchema[]; inspection: InspectionSchema }) =>
        this.subjectModelOfflineService
          .getSubjectModel(inspection.subject.subjectModelId, inspection.locationVersion)
          .pipe(map((subjectModel) => ({ images, subjectModel })))
      ),
      switchMap(({ images, subjectModel }) => {
        const validLocationIds = new Set(this.utils.getLocations(subjectModel, null).map((loc) => loc.identifier));

        const staleImages = images.filter((img) => img.subjectLocationId != null && !validLocationIds.has(img.subjectLocationId));
        staleImages.forEach((img) => (img.subjectLocationId = null));

        const persist$ = staleImages.length > 0 ? this.inspectionImageDbService.putList(staleImages) : of(null);
        return persist$.pipe(map(() => images));
      })
    );
  }

  // IM02 GET
  getInspectionImage(inspectionId: string, inspectionImageId: string): Observable<InspectionImage> {
    return zip(this.inspectionImageDbService.get(inspectionId, inspectionImageId), from(this.fileSysAccessService.getSaveBlobFolderPath())).pipe(
      mergeMap((results) => {
        const schema = results[0];
        const blobPath = results[1];
        return this.genInspectionImage(schema, blobPath);
      })
    );
  }

  // IM03 POST
  fileUploadRaw(
    inspectionImageCreate: InspectionImageCreate,
    file: File,
    uploadPath: string,
    audioFile?: File,
    mviqComment?: string,
    mviqTagFlagIsAvailable?: boolean,
    isMviqImage?: boolean
  ): Observable<HttpEvent<{}>> {
    let uploadSchema: InspectionImageSchema;
    let searchSchemaWithExtension: InspectionImageSchema;
    let searchSchemaWithoutExtension: InspectionImageSchema;
    let inspection: InspectionSchema;
    let mark: Mark;
    let subjectModelSchema: SubjectModelSchema;
    let subjectModel: SubjectModel;
    let folderName: string = null;
    let fileName: string = null;
    let isReplaced: boolean = false;
    let isTempUpload: boolean = false; // tempフォルダに画像をアップロードする場合、true
    let isUpdateVideoToImage: boolean = false; // ExIfの読み込みをスキップする場合、true
    let merry: MerryUploadAttributes | null = null;

    return this.inspectionDbService
      .getInspection(inspectionImageCreate.inspectionId, true)
      .pipe(
        switchMap((insp) => {
          inspection = insp;

          return zip(
            this.subjectModelOfflineService.getSubjectModel(inspection.subject.subjectModelId, inspection.locationVersion),
            this.markOfflineService.getMark(),
            this.merryUploadService.read(file)
          );
        })
      )
      .pipe(
        switchMap((values) => {
          subjectModel = values[0];
          mark = values[1];
          merry = values[2];
          subjectModelSchema = inspection.subject.subjectModel;

          const fileNameParts = uploadPath?.split("/").filter(Boolean).reverse();

          if (fileNameParts.length >= 2 && !inspectionImageCreate.locationName) {
            folderName = fileNameParts[1];
          }

          fileName = fileNameParts[0];

          const subjectLocation = this.subjectLocationOfflineService.getImageSubjectLocation(
            subjectModelSchema,
            folderName,
            inspectionImageCreate.locationName,
            inspectionImageCreate.folderPath
          );

          uploadSchema = this.genInspectionImageSchema(inspectionImageCreate.inspectionId, file);
          uploadSchema.subjectLocationId = subjectLocation?.identifier;
          uploadSchema.isDataPlate = subjectLocation?.isDataPlate ?? false;
          uploadSchema.fileName = this.genFileName(fileName, mark, merry?.mark);
          uploadSchema = this.setMetadata(uploadSchema, mark, file.name, isMviqImage, mviqTagFlagIsAvailable, merry);
          uploadSchema.bookMarkOption = JSON.stringify(
            inspectionImageCreate?.bookMarkOption?.length ? inspectionImageCreate.bookMarkOption : (merry?.bookmarks ?? [])
          );

          const termLocations = this.utils.getLocations(subjectModel, null);
          const locationFolder = this.getLocationFolder(termLocations, uploadSchema.subjectLocationId);
          uploadSchema = this.setFilePath(uploadSchema, inspection.title, locationFolder);
          uploadSchema.comment = merry?.comment ?? mviqComment;

          if (this.requiredImageConversionExtensionPattern.test(uploadSchema.fileName)) {
            const extension = this.requiredImageConversionExtensionPattern.exec(uploadSchema.fileName)[1];
            const updatedExtensionFileName = this.utils.changeExtension(uploadSchema.fileName, this.getConversionExtension(extension));
            const mimeType = this.getMimeType(uploadSchema);
            uploadSchema.mimeType = mimeType == "" ? uploadSchema.mimeType : mimeType;
            uploadSchema.originalMimeType = mimeType == "" ? uploadSchema.originalMimeType : mimeType;

            return zip(
              this.inspectionImageDbService.getByFileNameAndLocation(
                uploadSchema.inspectionId,
                uploadSchema.id,
                updatedExtensionFileName,
                uploadSchema.subjectLocationId
              ),
              this.inspectionImageDbService.getByFileNameAndLocationWithoutExtension(
                uploadSchema.inspectionId,
                uploadSchema.id,
                uploadSchema.fileName,
                uploadSchema.subjectLocationId
              )
            );
          } else {
            // 変換が不要な拡張子の場合
            return zip(
              this.inspectionImageDbService.getByFileNameAndLocation(
                uploadSchema.inspectionId,
                uploadSchema.id,
                uploadSchema.fileName,
                uploadSchema.subjectLocationId
              ),
              this.inspectionImageDbService.getByFileNameAndLocationWithoutExtension(
                uploadSchema.inspectionId,
                uploadSchema.id,
                uploadSchema.fileName,
                uploadSchema.subjectLocationId
              )
            );
          }
        })
      )
      .pipe(
        switchMap(async (target) => {
          searchSchemaWithExtension = target[0];
          searchSchemaWithoutExtension = target[1];

          if (
            searchSchemaWithExtension &&
            (inspectionImageCreate.uploadOption === UploadOption.None || inspectionImageCreate.uploadOption === UploadOption.Skip)
          ) {
            throw new HttpResponse({
              status: 409,
              body: {
                fileName: searchSchemaWithExtension.fileName,
                subjectLocationId: searchSchemaWithExtension.subjectLocationId,
                isWithoutExtension: false
              }
            });
          }
          // 拡張子を含まない同一ファイル名が存在する場合、エラーを返す
          if (
            searchSchemaWithoutExtension &&
            this.isImageAndVideo(searchSchemaWithoutExtension, uploadSchema) &&
            inspectionImageCreate.uploadOption === UploadOption.None
          ) {
            throw new HttpResponse({
              status: 409,
              body: {
                fileName: searchSchemaWithoutExtension.fileName,
                subjectLocationId: searchSchemaWithoutExtension.subjectLocationId,
                isWithoutExtension: true
              }
            });
          }

          if (searchSchemaWithExtension && inspectionImageCreate.uploadOption === UploadOption.KeepBoth) {
            // Rename && getByFilePath
            let tempName: string = this.genFileName(fileName, mark);

            let withoutMime: string = this.utils.getFilePathWithoutExtension(tempName);
            const tempMark = this.utils.getMarkFromFilenameSuffix(withoutMime, mark.items);
            const mime: string = this.utils.getExtension(tempName);
            withoutMime = this.utils.getFileNameWithoutMark(withoutMime, mark.items);

            let fileSequenceNumber: number = 0;
            let searchTargetImageSchema: InspectionImageSchema = null;
            do {
              fileSequenceNumber++;
              let searchTargetFileName: string = `${withoutMime} (${fileSequenceNumber})${tempMark}.${mime}`;
              if (this.requiredImageConversionExtensionPattern.test(searchTargetFileName)) {
                // Search as JPEG
                const extension = this.requiredImageConversionExtensionPattern.exec(uploadSchema.fileName)[1];
                searchTargetFileName = this.utils.changeExtension(searchTargetFileName, this.getConversionExtension(extension));
              }

              searchTargetImageSchema = await this.getByFileNameAndLocation(
                uploadSchema.inspectionId,
                uploadSchema.id,
                searchTargetFileName,
                uploadSchema.subjectLocationId
              );
            } while (searchTargetImageSchema);

            tempName = `${withoutMime} (${fileSequenceNumber})${tempMark}.${mime}`;
            uploadSchema.filePath = uploadSchema.filePath.replace(uploadSchema.fileName, tempName);
            uploadSchema.thumbFilePath = uploadSchema.thumbFilePath.replace(uploadSchema.fileName, tempName);
            uploadSchema.localFilePath = uploadSchema.localFilePath.replace(uploadSchema.fileName, tempName);
            uploadSchema.fileName = tempName;
          }

          if (searchSchemaWithExtension && inspectionImageCreate.uploadOption === UploadOption.Replace) {
            uploadSchema.id = searchSchemaWithExtension.id;
            uploadSchema.OfflineId = searchSchemaWithExtension.OfflineId;

            uploadSchema.imageDescription = searchSchemaWithExtension.imageDescription;
            uploadSchema.scopeModel = searchSchemaWithExtension.scopeModel;
            uploadSchema.datetime = searchSchemaWithExtension.datetime;
            uploadSchema.imageType = searchSchemaWithExtension.imageType;
            uploadSchema.orientation = searchSchemaWithExtension.orientation;
            uploadSchema.width = searchSchemaWithExtension.width;
            uploadSchema.height = searchSchemaWithExtension.height;
            uploadSchema.metadata = searchSchemaWithExtension.metadata;
            uploadSchema.vegaMeasurement = searchSchemaWithExtension.vegaMeasurement;
            uploadSchema.isMeasurementEnabled = MeasurementMetaDataService.getIsMeasurementEnabled(searchSchemaWithExtension);

            isReplaced = true;
          }

          if (inspectionImageCreate.audioFile) {
            uploadSchema.audioFilePath = `inspections/${uploadSchema.inspectionId}/audio/${uploadSchema.id}.wav`;
          }

          if (
            searchSchemaWithoutExtension &&
            this.isImageAndVideo(searchSchemaWithoutExtension, uploadSchema) &&
            inspectionImageCreate.uploadOption === UploadOption.Replace
          ) {
            const tmpSchema = JSON.parse(JSON.stringify(uploadSchema));
            if (uploadSchema.fileName.toLowerCase().endsWith("." + IMAGE_FILETYPE.EXTENSION_MP4)) {
              // 既存画像を動画で更新する場合
              isUpdateVideoToImage = true;
              uploadSchema.id = searchSchemaWithoutExtension.id;
              uploadSchema.fileName = tmpSchema.fileName;
              uploadSchema.filePath = tmpSchema.filePath;
              uploadSchema.localFilePath = tmpSchema.localFilePath;
              uploadSchema.mimeType = tmpSchema.mimeType;
              uploadSchema.originalMimeType = tmpSchema.originalMimeType;

              uploadSchema.thumbFilePath = searchSchemaWithoutExtension.thumbFilePath;
              uploadSchema.imageDescription = searchSchemaWithoutExtension.imageDescription;
              uploadSchema.scopeModel = searchSchemaWithoutExtension.scopeModel;
              uploadSchema.datetime = searchSchemaWithoutExtension.datetime;
              uploadSchema.imageType = searchSchemaWithoutExtension.imageType;
              uploadSchema.orientation = searchSchemaWithoutExtension.orientation;
              uploadSchema.width = searchSchemaWithoutExtension.width;
              uploadSchema.height = searchSchemaWithoutExtension.height;
              uploadSchema.metadata = searchSchemaWithoutExtension.metadata;
              uploadSchema.vegaMeasurement = searchSchemaWithoutExtension.vegaMeasurement;
              uploadSchema.isMeasurementEnabled = MeasurementMetaDataService.getIsMeasurementEnabled(searchSchemaWithExtension);

              uploadSchema.status = INSPECTION_IMAGE_STATUS_CODE.UPLOADING;
              uploadSchema.lastUpdatedBy = tmpSchema.lastUpdatedBy;
              uploadSchema.lastUpdateDate = tmpSchema.lastUpdateDate;
            } else {
              // 既存動画を画像で更新する場合
              isTempUpload = true;
              uploadSchema = JSON.parse(JSON.stringify(searchSchemaWithoutExtension));
              uploadSchema.lastUpdatedBy = tmpSchema.lastUpdatedBy;
              uploadSchema.status = INSPECTION_IMAGE_STATUS_CODE.UPLOADING;
              const tmpFileName = this.utils.changeExtension(uploadSchema.fileName, IMAGE_FILETYPE.EXTENSION_JPEG);
              uploadSchema.thumbFilePath = this.getThumbFilePath(uploadSchema.inspectionId, uploadSchema.id, tmpFileName);
            }
          }

          // データ登録 & ファイル変換 & ステータス更新
          return this.inspectionImageDbService.put(uploadSchema);
        })
      )
      .pipe(
        switchMap(() =>
          this.uploadManagementDbService.put(
            new UploadManagementSchema(
              uploadSchema.id,
              this.tenantId,
              UploadDataType.InspectionImageInfo,
              true,
              this.userId,
              inspectionImageCreate.inspectionId
            )
          )
        )
      )
      .pipe(
        switchMap(() => {
          let saveAudioFileOb: Observable<UploadManagementSchema> = of(null as UploadManagementSchema);
          let originalDeleteOb: Observable<boolean> = of(null as boolean);
          let modifiedDeleteOb: Observable<boolean> = of(null as boolean);
          let remeasuredDeleteOb: Observable<boolean> = of(null as boolean);
          if (uploadSchema.audioFilePath && inspectionImageCreate.audioFile) {
            saveAudioFileOb = this.imageAudioUpsert(uploadSchema, inspectionImageCreate.audioFile);
          }
          if (searchSchemaWithoutExtension) {
            if ((inspectionImageCreate.uploadOption === UploadOption.Replace && !isTempUpload) || isUpdateVideoToImage) {
              const observables = [
                defer(() =>
                  this.fileSysAccessService.deleteFileForce(this.genFilePath(searchSchemaWithoutExtension.localFilePath, USE_IMAGE_TYPE.ORIGINAL))
                )
              ];

              if (!isUpdateVideoToImage) {
                observables.push(
                  defer(() =>
                    this.fileSysAccessService.deleteFileForce(this.genFilePath(searchSchemaWithoutExtension.thumbFilePath, USE_IMAGE_TYPE.ORIGINAL))
                  )
                );
              }

              originalDeleteOb = forkJoin(observables).pipe(mergeMap((values) => of(values.every((x) => x))));
            }
            if (inspectionImageCreate.uploadOption === UploadOption.Replace && searchSchemaWithoutExtension.modifiedItems) {
              modifiedDeleteOb = forkJoin([
                defer(() =>
                  this.fileSysAccessService.deleteFileForce(this.genFilePath(searchSchemaWithoutExtension.localFilePath, USE_IMAGE_TYPE.RETOUCHED))
                ),
                defer(() =>
                  this.fileSysAccessService.deleteFileForce(this.genFilePath(searchSchemaWithoutExtension.thumbFilePath, USE_IMAGE_TYPE.RETOUCHED))
                )
              ]).pipe(mergeMap((values) => of(values.every((x) => x))));
            }
            if (inspectionImageCreate.uploadOption === UploadOption.Replace && searchSchemaWithoutExtension.measurementItems) {
              remeasuredDeleteOb = forkJoin([
                defer(() =>
                  this.fileSysAccessService.deleteFileForce(
                    this.genFilePath(searchSchemaWithoutExtension.localFilePath, USE_IMAGE_TYPE.REMEASUREMENT)
                  )
                ),
                defer(() =>
                  this.fileSysAccessService.deleteFileForce(
                    this.genFilePath(searchSchemaWithoutExtension.thumbFilePath, USE_IMAGE_TYPE.REMEASUREMENT)
                  )
                )
              ]).pipe(mergeMap((values) => of(values.every((x) => x))));
            }
          }

          return this.inspectionImageUpsert(uploadSchema, file, isTempUpload)
            .pipe(switchMapTo(saveAudioFileOb))
            .pipe(switchMapTo(originalDeleteOb))
            .pipe(switchMapTo(modifiedDeleteOb))
            .pipe(switchMapTo(remeasuredDeleteOb));
        })
      )
      .pipe(
        mergeMap(() => {
          if (!isReplaced) {
            return of(uploadSchema);
          }

          return this.stereoMeasurementImageOfflineService
            .deleteMeasurementIntermediateInfo(uploadSchema.inspectionId, uploadSchema.id)
            .pipe(mergeMap(() => of(uploadSchema)));
        })
      )
      .pipe(
        switchMap(() =>
          this.uploadManagementDbService.put(
            new UploadManagementSchema(
              uploadSchema.id,
              this.tenantId,
              UploadDataType.InspectionImageInfo,
              true,
              this.userId,
              inspectionImageCreate.inspectionId
            )
          )
        )
      )
      .pipe(switchMap(() => this.inspectionImageUpsert(uploadSchema, file, isTempUpload)))
      .pipe(
        switchMap(() => {
          return this.readInspectionImage(uploadSchema.inspectionId, uploadSchema.id, ReadImageOption.Upload);
        })
      )
      .pipe(switchMap(() => this.inspectionDbService.updateInspectionStatus(this.genInspectionStatusSchema(inspection))))
      .pipe(
        switchMap((inspectionSchema) => {
          if (!this.isAppOnline) {
            return of(uploadSchema);
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return of(uploadSchema);
              }

              return this.inspectionSchemaServerSync(inspectionSchema)
                .pipe(switchMap((_) => this.inspectionImageServerSync(uploadSchema)))
                .pipe(switchMap((schema) => this.inspectionImageDataServerSync(schema)));
            })
          );
        })
      )
      .pipe(map(() => new HttpResponse()));
  }

  // IM04 PUT
  patchInspectionImage(inspectionImageUpdate: InspectionImageUpdate): Observable<any> {
    const result = new InspectionImageSchemaUpdate();
    result.id = inspectionImageUpdate.id;
    result.inspectionId = inspectionImageUpdate.inspectionId;
    result.subjectLocationId = inspectionImageUpdate.subjectLocationId;
    result.comment = inspectionImageUpdate.comment;
    result.bookMarkOption = JSON.stringify(inspectionImageUpdate?.bookMarkOption);

    let blobPath = "";
    let reportSchemas: IndividualReportSchema[];
    let image: InspectionImageSchema;
    let marks: Mark;
    let inspection: Inspection;
    let images: InspectionImageSchema[];
    let termLocations: SubjectLocation[];

    let oldLocName: string;
    let newLocName: string;
    let isDataPlate: boolean;
    let isMarkUpdate: boolean;
    let isLocationUpdate: boolean;
    let oldPath: string;
    let newPath: string;
    let oldModifiedPath: string;
    let newModifiedPath: string;
    let oldRemeasuredPath: string;
    let newRemeasuredPath: string;

    let oldThumFilePath: string = "";
    let newThumFilePath: string = "";
    let oldModifiedThumFilePath: string = "";
    let newModifiedThumFilePath: string = "";
    let oldRemeasuredThumFilePath: string = "";
    let newRemeasuredThumFilePath: string = "";

    let createFolderOb: Observable<boolean> = of(true);
    let renameOriginOb: Observable<boolean> = of(true);
    let renameModifiedOb: Observable<boolean> = of(true);
    let renameRemeasuredOb: Observable<boolean> = of(true);

    let reportObs = of([] as IndividualReportSchema[]);
    return zip(
      this.inspectionImageDbService.get(inspectionImageUpdate.inspectionId, inspectionImageUpdate.id),
      this.markOfflineService.getMark(),
      from(this.fileSysAccessService.getSaveBlobFolderPath()),
      this.individualReportDbService.getByInspectionIds([inspectionImageUpdate.inspectionId]),
      this.inspectionOfflineService.getInspection(inspectionImageUpdate.inspectionId),
      this.inspectionImageDbService.getAll(inspectionImageUpdate.inspectionId)
    )
      .pipe(
        switchMap((values) => {
          image = values[0];
          marks = values[1];
          blobPath = values[2];
          reportSchemas = values[3];
          inspection = values[4];
          images = values[5];

          return this.subjectModelOfflineService.getSubjectModel(inspection.subjectModelId, inspection.locationVersion);
        })
      )
      .pipe(
        switchMap((subjectModel) => {
          result.metadata = this.getMetadataChangedMarkTitleAndTags(
            image,
            inspectionImageUpdate.metadata.mark,
            inspectionImageUpdate.metadata.title,
            inspectionImageUpdate.metadata.tags
          );
          termLocations = this.utils.getLocations(subjectModel, null);

          // 変更対象がDataPlateかどうか判別する
          isDataPlate = termLocations.some((loc) => loc.identifier === inspectionImageUpdate.subjectLocationId && loc.isDataPlate);

          // 変更対象が部位変更かどうか判別する
          isLocationUpdate = inspectionImageUpdate.subjectLocationId !== null;

          const oldName = image.fileName;
          let newName = image.fileName;
          let withOutMime = image.fileName.split(".")[0];
          const Mime = image.fileName.split(".")[1];
          withOutMime = this.utils.getFileNameWithoutMark(withOutMime, marks.items);
          isMarkUpdate = inspectionImageUpdate.metadata.mark !== null;
          if (isMarkUpdate || isLocationUpdate) {
            if (isMarkUpdate) {
              const markItem = marks.items.filter((x) => x.identifier > 0).find((x) => x.identifier === inspectionImageUpdate.metadata.mark);
              if (markItem) {
                const suffix = "_" + markItem.code;
                withOutMime = withOutMime + suffix;
              }
              newName = withOutMime + "." + Mime;
            } else {
              newName = withOutMime + "." + Mime;
            }

            oldLocName = this.getLocationFolder(termLocations, image.subjectLocationId);
            newLocName = this.getLocationFolder(termLocations, inspectionImageUpdate.subjectLocationId);

            result.fileName = newName;
            result.filePath = this.getFilePath(result.inspectionId, result.id, newName);
            result.thumbFilePath = this.getThumbFilePath(result.inspectionId, result.id, newName);
            result.localFilePath = this.getLocalFilePath(inspection.title, newLocName, newName, "");

            oldPath = blobPath + image.localFilePath;
            newPath = blobPath + result.localFilePath;
            oldThumFilePath = blobPath + image.thumbFilePath;
            newThumFilePath = blobPath + result.thumbFilePath;

            if (image.modifiedItems) {
              oldModifiedPath = blobPath + this.getLocalFilePath(inspection.title, oldLocName, oldName, this.MODIFIED_PREFIX);
              newModifiedPath = blobPath + this.getLocalFilePath(inspection.title, newLocName, newName, this.MODIFIED_PREFIX);
              oldModifiedThumFilePath = blobPath + this.getThumbFilePath(result.inspectionId, result.id, oldName, this.MODIFIED_PREFIX);
              newModifiedThumFilePath = blobPath + this.getThumbFilePath(result.inspectionId, result.id, newName, this.MODIFIED_PREFIX);
            }

            if (image.measurementItems) {
              oldRemeasuredPath = blobPath + this.getLocalFilePath(inspection.title, oldLocName, oldName, this.REMEASURED_PREFIX);
              newRemeasuredPath = blobPath + this.getLocalFilePath(inspection.title, newLocName, newName, this.REMEASURED_PREFIX);
              oldRemeasuredThumFilePath = blobPath + this.getThumbFilePath(result.inspectionId, result.id, oldName, this.REMEASURED_PREFIX);
              newRemeasuredThumFilePath = blobPath + this.getThumbFilePath(result.inspectionId, result.id, newName, this.REMEASURED_PREFIX);
            }
          }

          if (image.subjectLocationId !== result.subjectLocationId) {
            // 部位変更時、移動先部位Folderが無かった場合、作成する
            const segments = result.localFilePath.split("/");
            const folderPath = segments.slice(0, segments.length - 1).join("/");
            createFolderOb = from(this.fileSysAccessService.createFolder(folderPath));
          }

          return zip(
            this.inspectionImageDbService.getByFileNameAndLocation(result.inspectionId, result.id, result.fileName, result.subjectLocationId),
            createFolderOb
          );
        })
      )
      .pipe(
        switchMap((values) => {
          const target = values[0];
          if (target && inspectionImageUpdate.uploadOption === UploadOption.None) {
            throw new HttpResponse({
              status: 409,
              body: { fileName: target.fileName, subjectLocationId: target.subjectLocationId, isWithoutExtension: false }
            });
          }

          if (target && inspectionImageUpdate.uploadOption === UploadOption.KeepBoth) {
            const tempName: string = this.genFileName(result.fileName, marks);
            let withoutMime: string = this.utils.getFilePathWithoutExtension(tempName);
            const tempMark = this.utils.getMarkFromFilenameSuffix(withoutMime, marks.items);
            const mime: string = this.utils.getExtension(tempName);
            withoutMime = this.utils.getFileNameWithoutMark(withoutMime, marks.items);

            // Rename
            let fileSequenceNumber: number = 0;
            let isContinue = true;
            let searchTargetFileName: string;
            do {
              fileSequenceNumber++;
              searchTargetFileName = `${withoutMime} (${fileSequenceNumber})${tempMark}.${mime}`;
              isContinue = images.some(
                (_) =>
                  _.id !== inspectionImageUpdate.id &&
                  _.subjectLocationId === inspectionImageUpdate.subjectLocationId &&
                  _.fileName === searchTargetFileName
              );
            } while (isContinue);

            result.filePath = this.getFilePath(result.inspectionId, result.id, searchTargetFileName);
            result.thumbFilePath = this.getThumbFilePath(result.inspectionId, result.id, searchTargetFileName);
            result.localFilePath = this.getLocalFilePath(inspection.title, newLocName, searchTargetFileName, "");
            result.fileName = searchTargetFileName;
            newPath = blobPath + result.localFilePath;

            if (image.modifiedItems) {
              newModifiedPath = blobPath + this.getLocalFilePath(inspection.title, newLocName, searchTargetFileName, this.MODIFIED_PREFIX);
            }

            if (image.measurementItems) {
              newRemeasuredPath = blobPath + this.getLocalFilePath(inspection.title, newLocName, searchTargetFileName, this.REMEASURED_PREFIX);
            }
          }

          if (target && inspectionImageUpdate.uploadOption === UploadOption.Replace) {
            this.inspectionImageDbService.delete(target.inspectionId, target.id);
          }

          if (isMarkUpdate || isLocationUpdate) {
            renameOriginOb = forkJoin([
              defer(() => this.fileSysAccessService.renameFilePath(oldPath, newPath)),
              defer(() => this.fileSysAccessService.renameFilePath(oldThumFilePath, newThumFilePath))
            ]).pipe(mergeMap((values) => of(values.every((x) => x))));
            if (image.modifiedItems) {
              renameModifiedOb = forkJoin([
                defer(() => this.fileSysAccessService.renameFilePath(oldModifiedPath, newModifiedPath)),
                defer(() => this.fileSysAccessService.renameFilePath(oldModifiedThumFilePath, newModifiedThumFilePath))
              ]).pipe(mergeMap((values) => of(values.every((x) => x))));
            }
            if (image.measurementItems) {
              renameRemeasuredOb = forkJoin([
                defer(() => this.fileSysAccessService.renameFilePath(oldRemeasuredPath, newRemeasuredPath)),
                defer(() => this.fileSysAccessService.renameFilePath(oldRemeasuredThumFilePath, newRemeasuredThumFilePath))
              ]).pipe(mergeMap((values) => of(values.every((x) => x))));
            }
          }

          if (image.subjectLocationId !== result.subjectLocationId) {
            reportObs = this.individualReportDbService.putALL(this.updateReportLocations(reportSchemas, image, result.subjectLocationId));
          }
          result.isDataPlate = isDataPlate;

          return zip(this.inspectionImageDbService.put(result), renameOriginOb, renameModifiedOb, renameRemeasuredOb, reportObs);
        })
      )
      .pipe(
        switchMap((values) => {
          const imageSchema = values[0];
          if (!this.isAppOnline) {
            return zip(of(imageSchema), of(reportSchemas));
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return zip(of(imageSchema), of(reportSchemas));
              }

              let inspectionImage: InspectionImage;
              return this.inspectionImageOnlineService
                .patchInspectionImage(inspectionImageUpdate)
                .pipe(
                  switchMap((response) => {
                    inspectionImage = response.inspectionImage;
                    if (reportSchemas.length < 1) {
                      return of([] as InspectionReport[]);
                    }
                    // 関連レポートの取得
                    const getReportOfs = reportSchemas.map((schema) => this.inspectionReportOnlineService.getInspectionReport(schema.reportId));
                    return zip(...getReportOfs);
                  })
                )
                .pipe(
                  switchMap((inspectionReports) => {
                    imageSchema.lastUpdateDate = inspectionImage.lastUpdateDate;

                    let reportObs: Observable<IndividualReportSchema[]>;
                    if (reportSchemas.length > 0) {
                      reportSchemas.forEach((schema) => {
                        const target = inspectionReports.find((report) => report.id === schema.id);
                        if (target) {
                          schema.lastUpdateDate = target.lastUpdateDate;
                          schema.reportLocations = JSON.stringify(target.reportLocations);
                        }
                      });
                      reportObs = this.individualReportDbService.putALL(reportSchemas);
                    } else {
                      reportObs = of([] as IndividualReportSchema[]);
                    }

                    return zip(this.inspectionImageDbService.put(imageSchema), reportObs);
                  })
                );
            })
          );
        })
      )
      .pipe(
        mergeMap((values) => {
          const schema = values[0];
          return this.genInspectionImage(schema, blobPath);
        }),
        map((value) => {
          const inspectionImage: InspectionImage = value;
          return {
            inspectionImage: inspectionImage,
            updatedReports: []
          };
        })
      );
  }

  // IM05 PUT
  patchInspectionImageMultiple(inspectionId: string, inspectionImageMultipleUpdate: InspectionImageMultipleUpdate): Observable<any> {
    const imageIds = inspectionImageMultipleUpdate.imageIds.map((item) => item.id);

    // IDを元に画像情報を取得
    let inspection: Inspection;
    let schemas: InspectionImageSchema[];
    let mark: Mark;
    let blobPath = "";
    let reportSchemas: IndividualReportSchema[];
    const conflictList = [];
    let newLocName: string;
    let oldLocName: string;

    return zip(
      this.inspectionOfflineService.getInspection(inspectionId),
      this.inspectionImageDbService.getList(imageIds, true, inspectionId),
      this.markOfflineService.getMark(),
      from(this.fileSysAccessService.getSaveBlobFolderPath()),
      this.individualReportDbService.getByInspectionIds([inspectionId])
    )
      .pipe(
        switchMap((values) => {
          inspection = values[0];
          schemas = values[1];
          mark = values[2];
          blobPath = values[3];
          reportSchemas = values[4];

          return this.subjectModelOfflineService.getSubjectModel(inspection.subjectModelId, inspection.locationVersion);
        })
      )
      .pipe(
        switchMap((subjectModel) => {
          // 画像情報を更新するObservableの配列を作成
          const termLocations = this.utils.getLocations(subjectModel, null);

          // 変更対象がDataPlateかどうか判別する
          const isDataPlate = termLocations.some((loc) => loc.identifier === inspectionImageMultipleUpdate.subjectLocationId && loc.isDataPlate);

          let firstCheckFlag = true;
          const isMarkUpdate = inspectionImageMultipleUpdate.metadata.mark !== null;
          const isLocationUpdate = inspectionImageMultipleUpdate.subjectLocationId !== null;
          const subs = schemas.map((schema) => {
            let newPath: string;
            let oldPath: string;
            let newModifiedPath: string;
            let oldModifiedPath: string;
            let newRemeasuredPath: string;
            let oldRemeasuredPath: string;

            let oldThumFilePath: string = "";
            let newThumFilePath: string = "";
            let oldModifiedThumFilePath: string = "";
            let newModifiedThumFilePath: string = "";
            let oldRemeasuredThumFilePath: string = "";
            let newRemeasuredThumFilePath: string = "";

            const result: InspectionImageSchemaUpdate = new InspectionImageSchemaUpdate();
            result.id = schema.id;
            result.inspectionId = schema.inspectionId;

            // 異なるsubjectLocationId変更する場合orderをnullにする
            result.sortOrder =
              isLocationUpdate && result.subjectLocationId === inspectionImageMultipleUpdate.subjectLocationId ? result.sortOrder : null;

            // subjectLocationIdの指定がある場合のみ変更する
            result.subjectLocationId = isLocationUpdate ? inspectionImageMultipleUpdate.subjectLocationId : schema.subjectLocationId;

            // subjectLocationIdの指定がある場合のみ変更する
            result.isDataPlate = isLocationUpdate ? isDataPlate : schema.isDataPlate;

            // コメントがnullの場合、更新をしない
            result.comment = inspectionImageMultipleUpdate.comment === null ? schema.comment : inspectionImageMultipleUpdate.comment;

            // metadataの指定がある場合のみ変更する
            if (
              inspectionImageMultipleUpdate.metadata.mark !== null ||
              inspectionImageMultipleUpdate.metadata.title ||
              inspectionImageMultipleUpdate.metadata.tags
            ) {
              const tempMetaData: Metadata = JSON.parse(schema.metadata) as Metadata;
              tempMetaData.mark =
                inspectionImageMultipleUpdate.metadata.mark !== null ? inspectionImageMultipleUpdate.metadata.mark : tempMetaData.mark;
              tempMetaData.title = inspectionImageMultipleUpdate.metadata.title ? inspectionImageMultipleUpdate.metadata.title : tempMetaData.title;
              tempMetaData.tags =
                inspectionImageMultipleUpdate.metadata.tags !== null ? inspectionImageMultipleUpdate.metadata.tags : tempMetaData.tags;

              result.metadata = this.getMetadataChangedMarkTitleAndTags(schema, tempMetaData.mark, tempMetaData.title, tempMetaData.tags);
            } else {
              result.metadata = schema.metadata;
            }

            let createFolderOb = of(true);
            let renameOriginOb = of(true);
            let renameModifiedOb = of(true);
            let renameRemeasuredOb = of(true);
            const oldName = schema.fileName;
            let newName = schema.fileName;

            let withOutMime = schema.fileName.split(".")[0];
            const Mime = schema.fileName.split(".")[1];
            withOutMime = this.utils.getFileNameWithoutMark(withOutMime, mark.items);

            if (isMarkUpdate) {
              const markItem = mark.items.filter((x) => x.identifier > 0).find((x) => x.identifier === inspectionImageMultipleUpdate.metadata.mark);
              if (markItem) {
                const suffix = "_" + markItem.code;
                withOutMime = withOutMime + suffix;
              }
              newName = withOutMime + "." + Mime;
            }

            oldLocName = this.getLocationFolder(termLocations, schema.subjectLocationId);
            newLocName = this.getLocationFolder(
              termLocations,
              isLocationUpdate ? inspectionImageMultipleUpdate.subjectLocationId : schema.subjectLocationId
            );

            result.fileName = newName;
            result.filePath = this.getFilePath(result.inspectionId, result.id, newName);
            result.thumbFilePath = this.getThumbFilePath(result.inspectionId, result.id, newName);
            result.localFilePath = this.getLocalFilePath(inspection.title, newLocName, newName, "");

            oldPath = blobPath + schema.localFilePath;
            newPath = blobPath + result.localFilePath;
            oldThumFilePath = blobPath + schema.thumbFilePath;
            newThumFilePath = blobPath + result.thumbFilePath;

            if (schema.modifiedItems) {
              oldModifiedPath = blobPath + this.getLocalFilePath(inspection.title, oldLocName, oldName, this.MODIFIED_PREFIX);
              newModifiedPath = blobPath + this.getLocalFilePath(inspection.title, newLocName, newName, this.MODIFIED_PREFIX);
              oldModifiedThumFilePath = blobPath + this.getThumbFilePath(result.inspectionId, result.id, oldName, this.MODIFIED_PREFIX);
              newModifiedThumFilePath = blobPath + this.getThumbFilePath(result.inspectionId, result.id, newName, this.MODIFIED_PREFIX);
            }

            if (schema.measurementItems) {
              oldRemeasuredPath = blobPath + this.getLocalFilePath(inspection.title, oldLocName, oldName, this.REMEASURED_PREFIX);
              newRemeasuredPath = blobPath + this.getLocalFilePath(inspection.title, newLocName, newName, this.REMEASURED_PREFIX);
              oldRemeasuredThumFilePath = blobPath + this.getThumbFilePath(result.inspectionId, result.id, oldName, this.REMEASURED_PREFIX);
              newRemeasuredThumFilePath = blobPath + this.getThumbFilePath(result.inspectionId, result.id, newName, this.REMEASURED_PREFIX);
            }

            if (isLocationUpdate) {
              // 部位変更時、移動先部位Folderが無い場合Folderを作成する
              const segments = result.localFilePath.split("/");
              const folderPath = segments.slice(0, segments.length - 1).join("/");
              createFolderOb = from(this.fileSysAccessService.createFolder(folderPath));
            }

            return zip(
              this.inspectionImageDbService.getByFileNameAndLocation(result.inspectionId, result.id, result.fileName, result.subjectLocationId),
              createFolderOb
            ).pipe(
              switchMap((values) => {
                const target = values[0];
                let putResult = target ? schema : result;
                if (target && target.id !== schema.id) {
                  conflictList.push({
                    id: result.id,
                    fileName: result.fileName,
                    subjectLocationId: result.subjectLocationId,
                    isWithoutExtension: false
                  });
                  newPath = oldPath;
                  newModifiedPath = oldModifiedPath;
                  newRemeasuredPath = oldRemeasuredPath;
                } else if (
                  schemas.some(
                    (_) =>
                      _.id !== schema.id &&
                      ((isMarkUpdate &&
                        this.utils.getFileNameWithoutMark(_.fileName.split(".")[0], mark.items) ===
                          this.utils.getFileNameWithoutMark(result.fileName.split(".")[0], mark.items) &&
                        _.subjectLocationId === result.subjectLocationId) ||
                        (!isMarkUpdate && _.fileName === schema.fileName))
                  )
                ) {
                  if (!firstCheckFlag) {
                    const file = isMarkUpdate
                      ? schemas.find(
                          (_) =>
                            _.id !== schema.id &&
                            this.utils.getFileNameWithoutMark(_.fileName.split(".")[0], mark.items) ===
                              this.utils.getFileNameWithoutMark(result.fileName.split(".")[0], mark.items) &&
                            _.subjectLocationId === result.subjectLocationId
                        )
                      : schemas.find((_) => _.id !== schema.id && _.fileName === schema.fileName);
                    conflictList.push({
                      id: result.id,
                      fileName: file.fileName,
                      subjectLocationId: result.subjectLocationId,
                      isWithoutExtension: false
                    });
                    putResult = schema;
                    newPath = oldPath;
                    newModifiedPath = oldModifiedPath;
                    newRemeasuredPath = oldRemeasuredPath;
                  } else {
                    firstCheckFlag = false;
                    putResult = result;
                  }
                }
                if (isMarkUpdate || isLocationUpdate) {
                  renameOriginOb = forkJoin([
                    defer(() => this.fileSysAccessService.renameFilePath(oldPath, newPath)),
                    defer(() => this.fileSysAccessService.renameFilePath(oldThumFilePath, newThumFilePath))
                  ]).pipe(mergeMap((values) => of(values.every((x) => x))));
                  if (schema.modifiedItems) {
                    renameModifiedOb = forkJoin([
                      defer(() => this.fileSysAccessService.renameFilePath(oldModifiedPath, newModifiedPath)),
                      defer(() => this.fileSysAccessService.renameFilePath(oldModifiedThumFilePath, newModifiedThumFilePath))
                    ]).pipe(mergeMap((values) => of(values.every((x) => x))));
                  }
                  if (schema.measurementItems) {
                    renameRemeasuredOb = forkJoin([
                      defer(() => this.fileSysAccessService.renameFilePath(oldRemeasuredPath, newRemeasuredPath)),
                      defer(() => this.fileSysAccessService.renameFilePath(oldRemeasuredThumFilePath, newRemeasuredThumFilePath))
                    ]).pipe(mergeMap((values) => of(values.every((x) => x))));
                  }
                }

                return zip(this.inspectionImageDbService.put(putResult), renameOriginOb, renameModifiedOb, renameRemeasuredOb);
              })
            );
          });

          let reportObs = of([] as IndividualReportSchema[]);
          if (inspectionImageMultipleUpdate.subjectLocationId) {
            schemas.forEach((image) => {
              if (image.subjectLocationId !== inspectionImageMultipleUpdate.subjectLocationId) {
                reportSchemas = this.updateReportLocations(reportSchemas, image, inspectionImageMultipleUpdate.subjectLocationId);
              }
            });
            reportObs = this.individualReportDbService.putALL(reportSchemas);
          }

          return zip(zip(...subs), reportObs);
        })
      )
      .pipe(
        switchMap((values) => {
          const updateImageResults = values[0];
          const imageSchemas = updateImageResults.map((_) => _[0]);
          const reportSchemas = values[1];

          if (!this.isAppOnline) {
            return zip(of(imageSchemas), of(reportSchemas));
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return zip(of(imageSchemas), of(reportSchemas));
              }

              let successImages: any[];
              let updatedReports: any[];
              return this.inspectionImageOnlineService
                .patchInspectionImageMultiple(inspectionId, inspectionImageMultipleUpdate)
                .pipe(
                  switchMap((res) => {
                    successImages = res.success;
                    updatedReports = res.updatedReports;
                    if (updatedReports.length < 1) {
                      return of([] as InspectionReport[]);
                    }

                    // 関連レポートの取得
                    const getReportOfs = updatedReports.map((report) => this.inspectionReportOnlineService.getInspectionReport(report.id));
                    return zip(...getReportOfs);
                  })
                )
                .pipe(
                  switchMap((inspectionReports) => {
                    successImages.forEach((successImage) => {
                      imageSchemas.some((schema) => {
                        return schema.id === successImage.id;
                      });
                    });

                    reportSchemas.forEach((schema) => {
                      const target = inspectionReports.find((report) => report.id === schema.id);
                      if (target) {
                        schema.lastUpdateDate = target.lastUpdateDate;
                        schema.reportLocations = JSON.stringify(target.reportLocations);
                      }
                    });

                    return zip(this.inspectionImageDbService.putList(imageSchemas), this.individualReportDbService.putALL(reportSchemas));
                  })
                );
            })
          );
        })
      )
      .pipe(
        map((results) => {
          const updateImageResults = results[0];
          // 更新後レスポンスを整形
          return {
            success: updateImageResults.map((inspImageSchema) => {
              return {
                id: inspImageSchema.id
              };
            }),
            error: [],
            updatedReports: [],
            conflict: conflictList
          };
        })
      );
  }

  // IM06 DELETE
  deleteInspectionImage(inspectionId: string, inspectionImageId: string): Observable<any> {
    let target: InspectionImageSchema;
    let reportSchemas: IndividualReportSchema[];
    return zip(
      this.inspectionImageDbService.get(inspectionId, inspectionImageId), // 削除対象取得
      this.individualReportDbService.getByInspectionIds([inspectionId])
    )
      .pipe(
        switchMap((values) => {
          // 削除
          target = values[0];
          reportSchemas = values[1];
          const schemaDeleteOb = this.inspectionImageDbService
            .delete(target.inspectionId, target.id)
            .pipe(switchMapTo(this.uploadManagementDbService.deleted(target.id, UploadDataType.InspectionImageInfo)));
          const binaryDeleteOb = forkJoin([
            defer(() => this.fileSysAccessService.deleteFileForce(this.genFilePath(target.localFilePath, USE_IMAGE_TYPE.ORIGINAL))),
            defer(() => this.fileSysAccessService.deleteFileForce(this.genFilePath(target.thumbFilePath, USE_IMAGE_TYPE.ORIGINAL)))
          ]).pipe(switchMapTo(this.uploadManagementDbService.deleted(target.id, UploadDataType.InspectionImageFile)));
          const modifiedDeleteOb = target.modifiedItems
            ? forkJoin([
                defer(() => this.fileSysAccessService.deleteFileForce(this.genFilePath(target.localFilePath, USE_IMAGE_TYPE.RETOUCHED))),
                defer(() => this.fileSysAccessService.deleteFileForce(this.genFilePath(target.thumbFilePath, USE_IMAGE_TYPE.RETOUCHED)))
              ]).pipe(switchMapTo(this.uploadManagementDbService.deleted(target.id, UploadDataType.InspectionModifiedImageFile)))
            : of(null as UploadManagementSchema);
          const remeasuredDeleteOb = target.measurementItems
            ? forkJoin([
                defer(() => this.fileSysAccessService.deleteFileForce(this.genFilePath(target.localFilePath, USE_IMAGE_TYPE.REMEASUREMENT))),
                defer(() => this.fileSysAccessService.deleteFileForce(this.genFilePath(target.thumbFilePath, USE_IMAGE_TYPE.REMEASUREMENT)))
              ]).pipe(switchMapTo(this.uploadManagementDbService.deleted(target.id, UploadDataType.InspectionMeasurementImageFile)))
            : of(null as UploadManagementSchema);
          const measurementIntermediateInfoOb: Observable<boolean> = this.stereoMeasurementImageOfflineService.deleteMeasurementIntermediateInfo(
            target.inspectionId,
            target.id
          );

          reportSchemas = this.updateReportLocations(reportSchemas, target, null);
          const reportLocationObs = this.individualReportDbService.putALL(reportSchemas);
          return zip(schemaDeleteOb, binaryDeleteOb, modifiedDeleteOb, remeasuredDeleteOb, measurementIntermediateInfoOb, reportLocationObs);
        })
      )
      .pipe(
        switchMap(() => {
          // 削除画像のロケーション画像を一覧取得
          return this.getInspectionImages(inspectionId, null);
        })
      )
      .pipe(
        switchMap((images) => {
          // 画像のソートオーダーを振りなおし
          if (images.length < 1) {
            return of(true);
          }
          const orderList = images.map((img, i) => {
            const order = target.subjectLocationId ? i + 1 : null;
            return new InspectionImageOrder(img.id, order);
          });

          const order = new InspectionImageOrderChange(inspectionId, target.subjectLocationId, orderList);
          return this.patchInspectionImageOrderChange(inspectionId, order, true).pipe(
            switchMap(() => this.individualReportDeleteSync(inspectionId, inspectionImageId, reportSchemas))
          );
        })
      )
      .pipe(
        map(() => {
          // 返却レスポンス作成
          return {
            inspectionImageId: inspectionImageId,
            updatedReports: []
          };
        })
      );
  }

  // IM07 DELETE
  deleteInspectionImageMultiple(inspectionId: string, inspectionImageIdDeleteList: string[]): Observable<any> {
    const getSubs = inspectionImageIdDeleteList.map((imageId) => this.inspectionImageDbService.get(inspectionId, imageId));
    let targets: InspectionImageSchema[];
    let subjectLocationId: string;
    let reportSchemas: IndividualReportSchema[];
    return zip(
      zip(...getSubs), // 削除対象の取得
      this.individualReportDbService.getByInspectionIds([inspectionId])
    )
      .pipe(
        switchMap((values) => {
          targets = values[0];
          reportSchemas = values[1];

          const schemaDeleteSubs = this.inspectionImageDbService
            .deleteList(inspectionId, inspectionImageIdDeleteList)
            .pipe(switchMap(() => this.uploadManagementDbService.deletedList(inspectionImageIdDeleteList, UploadDataType.InspectionImageInfo)));

          const deleteImgObs = targets.map((img) => {
            return from(this.fileSysAccessService.deleteFileForce(this.genFilePath(img.localFilePath, USE_IMAGE_TYPE.ORIGINAL))).pipe(
              switchMapTo(from(this.fileSysAccessService.deleteFileForce(this.genFilePath(img.thumbFilePath, USE_IMAGE_TYPE.ORIGINAL)))),
              switchMapTo(this.uploadManagementDbService.deleted(img.id, UploadDataType.InspectionImageFile))
            );
          });

          const deleteModifiedImgObs = targets.map((img) => {
            return img.modifiedItems
              ? from(this.fileSysAccessService.deleteFileForce(this.genFilePath(img.localFilePath, USE_IMAGE_TYPE.RETOUCHED))).pipe(
                  switchMapTo(from(this.fileSysAccessService.deleteFileForce(this.genFilePath(img.thumbFilePath, USE_IMAGE_TYPE.RETOUCHED)))),
                  switchMapTo(this.uploadManagementDbService.deleted(img.id, UploadDataType.InspectionModifiedImageFile))
                )
              : of(null as UploadManagementSchema);
          });

          const deleteRemeasuredImgObs = targets.map((img) => {
            return img.measurementItems
              ? from(this.fileSysAccessService.deleteFileForce(this.genFilePath(img.localFilePath, USE_IMAGE_TYPE.REMEASUREMENT))).pipe(
                  switchMapTo(from(this.fileSysAccessService.deleteFileForce(this.genFilePath(img.thumbFilePath, USE_IMAGE_TYPE.REMEASUREMENT)))),
                  switchMapTo(this.uploadManagementDbService.deleted(img.id, UploadDataType.InspectionMeasurementImageFile))
                )
              : of(null as UploadManagementSchema);
          });

          const deleteMeasurementIntermediateInfoObs: Observable<boolean> =
            this.stereoMeasurementImageOfflineService.deleteMeasurementIntermediateInfos(inspectionId, inspectionImageIdDeleteList);

          let reportObs = of([] as IndividualReportSchema[]);
          if (reportSchemas.length > 0) {
            targets.forEach((image) => {
              reportSchemas = this.updateReportLocations(reportSchemas, image, null);
            });
            reportObs = this.individualReportDbService.putALL(reportSchemas);
          }

          return zip(
            schemaDeleteSubs,
            ...deleteImgObs,
            ...deleteModifiedImgObs,
            ...deleteRemeasuredImgObs,
            deleteMeasurementIntermediateInfoObs,
            reportObs
          );
        })
      )
      .pipe(
        switchMap(() => {
          subjectLocationId = targets[0].subjectLocationId;
          return this.getInspectionImages(inspectionId, this.filterStateService.inspectionImageFilters);
        })
      )
      .pipe(
        switchMap((images) => {
          // 画像のソートオーダーを振りなおし
          if (!images || images.length < 1) {
            return of(null);
          }

          const orderList = images.map((img, i) => {
            const order = subjectLocationId ? i + 1 : null;
            return new InspectionImageOrder(img.id, order);
          });
          return this.patchInspectionImageOrderChange(inspectionId, new InspectionImageOrderChange(inspectionId, subjectLocationId, orderList), true);
        })
      )
      .pipe(switchMap(() => this.individualReportMultiDeleteSync(inspectionId, inspectionImageIdDeleteList, reportSchemas)))
      .pipe(
        map(() => {
          // 返却レスポンス作成
          return {
            error: [],
            updatedReports: []
          };
        })
      );
  }

  // IM08 PUT
  patchInspectionImageOrderChange(inspectionId: string, imageOrders: InspectionImageOrderChange, isOtherMethodCalled = false): Observable<any> {
    let blobPath: string;
    // IDを元に画像情報を取得
    const imageIds = imageOrders.imagesSortOrder.map((order) => order.id);
    return this.inspectionImageDbService
      .getList(imageIds, true, inspectionId)
      .pipe(
        switchMap((schemas) => {
          // 画像情報を更新するObservableの配列を作成
          const subs = schemas.map((schema) => {
            const orderObj = imageOrders.imagesSortOrder.find((order) => order.id === schema.id);
            const result = new InspectionImageSchemaUpdate();
            result.id = schema.id;
            result.inspectionId = schema.inspectionId;
            result.subjectLocationId = schema.subjectLocationId;
            result.comment = schema.comment;
            result.metadata = schema.metadata;
            result.sortOrder = orderObj.sortOrder;
            return zip(this.inspectionImageDbService.put(result), from(this.fileSysAccessService.getSaveBlobFolderPath()));
          });

          return zip(...subs);
        })
      )
      .pipe(
        switchMap((values) => {
          blobPath = values[0][1];
          const imageSchemas = values.map((_) => _[0]);
          if (!this.isAppOnline || isOtherMethodCalled) {
            return of(imageSchemas);
          }

          return this.inspectionImageOnlineService.patchInspectionImageOrderChange(inspectionId, imageOrders).pipe(
            switchMap((images) => {
              imageSchemas.forEach((schema) => {
                const target = images.find((image) => image.id === schema.id);
                if (target) {
                  schema.sortOrder = target.sortOrder;
                }
              });
              return this.inspectionImageDbService.putList(imageSchemas);
            })
          );
        })
      )
      .pipe(
        map((imageInfos) => {
          const inspectionImageList = imageInfos
            .map((imageInfo) => {
              const schema = imageInfo;
              const result = new InspectionImageListItem();
              result.id = schema.id;
              result.inspectionId = schema.inspectionId;
              result.subjectLocationId = schema.subjectLocationId;
              result.mimeType = schema.mimeType;
              result.datetime = null; // 画像の撮影日
              result.fileName = schema.fileName;
              result.thumbFilePath =
                FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.localFilePath, schema.usedImageMode) + this.genCacheParam();
              result.permission = this.getPermission(schema);
              result.status = schema.status;

              const metadata = JSON.parse(schema.metadata) as Metadata;
              result.mark = metadata.mark;
              result.title = metadata.title;
              result.tags = metadata.tags;
              result.comment = schema.comment;
              result.isDataPlate = schema.isDataPlate;
              result.usedImageMode = schema.usedImageMode;
              result.hasModifiedImage = schema.modifiedItems !== null;
              result.hasMeasurementImage = schema.measurementItems !== null;
              result.sortOrder = schema.sortOrder;
              return result;
            })
            .sort((img1, img2) => this.orderSort(img1, img2));

          return inspectionImageList;
        })
      );
  }

  // IM09 PUT
  putInspImageModifiedUse(modifiedUse: ModifiedImageUseUpdate): Observable<InspectionImage> {
    let blobPath: string;
    let imageSchema: InspectionImageSchema;
    return zip(this.inspectionImageDbService.putInspImageModifiedUse(modifiedUse), from(this.fileSysAccessService.getSaveBlobFolderPath())).pipe(
      switchMap((values) => {
        imageSchema = values[0];
        blobPath = values[1];
        if (!this.isAppOnline) {
          return of(imageSchema);
        }

        return this.inspectionImageOnlineService.putInspImageModifiedUse(modifiedUse).pipe(
          switchMap((response) => {
            imageSchema.lastUpdateDate = response.lastUpdateDate;
            return this.inspectionImageDbService.put(imageSchema);
          })
        );
      }),
      mergeMap((imageSchema) => this.genInspectionImage(imageSchema, blobPath))
    );
  }

  // IM10
  getImageBinaryUrl(inspectionId: string, inspectionImageId: string, imageNumber: number): Promise<string> {
    return zip(this.inspectionImageDbService.get(inspectionId, inspectionImageId), from(this.fileSysAccessService.getSaveBlobFolderPath()))
      .pipe(
        map((results) => {
          const schema = results[0];
          const blobPath = results[1];

          if (imageNumber === 1) {
            return FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.localFilePath, USE_IMAGE_TYPE.RETOUCHED) + this.genCacheParam();
          } else if (imageNumber === 2) {
            return (
              FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.localFilePath, USE_IMAGE_TYPE.REMEASUREMENT) + this.genCacheParam()
            );
          } else {
            return FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.localFilePath, USE_IMAGE_TYPE.ORIGINAL) + this.genCacheParam();
          }
        })
      )
      .toPromise();
  }

  createInspectionImages(inspectionId: string, inspectionImage: InspectionImageCreate[]) {
    return of();
  }

  /**
   * Retrieves the full paths for the local file, temporary file, and thumbnail file.
   * @param imageSchema The schema containing image details.
   * @param tmpFolderPath The temporary folder path.
   * @returns An Observable of a tuple: [full path of local file, temporary file array, full path of thumbnail file].
   */
  private getFilePaths(imageSchema: InspectionImageSchema, tmpFolderPath: string): Observable<[string, string[], string]> {
    return zip(
      defer(() => this.fileSysAccessService.getFileFullPath(imageSchema.localFilePath)),
      defer(() => this.inspectionImageDbService.getTmpFile(tmpFolderPath)),
      defer(() => this.fileSysAccessService.getFileFullPath(imageSchema.thumbFilePath))
    );
  }

  /**
   * Processes the file for JPEG conversion if needed, or simply retrieves file paths.
   * @param imageSchema The schema containing image details.
   * @param tmpFolderPath The temporary folder path.
   * @returns An Observable of a tuple containing:
   *          [full path of local file, temporary file array, full path of thumbnail file].
   */
  private processJpegConversion(imageSchema: InspectionImageSchema, tmpFolderPath: string): Observable<[string, string[], string]> {
    if (!this.requiredImageConversionExtensionPattern.test(imageSchema.fileName)) {
      return this.getFilePaths(imageSchema, tmpFolderPath);
    }

    // Else file requires conversion to JPEG.
    return defer(() => this.fileSysAccessService.getFileFullPath(imageSchema.localFilePath)).pipe(
      switchMap((fileFullPath) => {
        // Construct the new JPEG file path.
        const convertedImageFilePath = fileFullPath.replace(this.requiredImageConversionExtensionPattern, "." + IMAGE_FILETYPE.EXTENSION_JPEG);
        // Convert the file to JPEG.
        return this.offlineUtilsService.convertToJpeg(fileFullPath, convertedImageFilePath).pipe(
          map(() => {
            // Update the image schema with new JPEG information.
            imageSchema.fileName = this.utils.changeExtension(imageSchema.fileName, IMAGE_FILETYPE.EXTENSION_JPEG);
            imageSchema.filePath = this.utils.changeExtension(imageSchema.filePath, IMAGE_FILETYPE.EXTENSION_JPEG);
            imageSchema.thumbFilePath = this.utils.changeExtension(imageSchema.thumbFilePath, IMAGE_FILETYPE.EXTENSION_JPEG);
            imageSchema.localFilePath = this.utils.changeExtension(imageSchema.localFilePath, IMAGE_FILETYPE.EXTENSION_JPEG);
            imageSchema.mimeType = IMAGE_FILETYPE.JPEG;
            return convertedImageFilePath;
          })
        );
      }),
      switchMap(() => this.getFilePaths(imageSchema, tmpFolderPath))
    );
  }

  /**
   * Processes the file for AVI conversion to MP4 if needed, or simply retrieves file paths.
   * @param imageSchema The schema containing image details.
   * @param tmpFolderPath The temporary folder path.
   * @returns An Observable of a tuple containing:
   *          [full path of local file, temporary file array, full path of thumbnail file].
   */
  private processAviConversionToMp4(imageSchema: InspectionImageSchema, tmpFolderPath: string): Observable<[string, string[], string]> {
    if (!this.requiredAviConversionExtensionPattern.test(imageSchema.fileName)) {
      return this.getFilePaths(imageSchema, tmpFolderPath);
    }
    // Else file requires conversion from AVI to MP4.
    return defer(() => this.fileSysAccessService.getFileFullPath(imageSchema.localFilePath)).pipe(
      switchMap((fileFullPath) => {
        // Construct the new MP4 file path.
        const convertedVideoFilePath = fileFullPath.replace(this.requiredAviConversionExtensionPattern, "." + IMAGE_FILETYPE.EXTENSION_MP4);
        // Convert the file to MP4.
        return this.offlineUtilsService.processAviConversionToMp4(fileFullPath, convertedVideoFilePath).pipe(
          switchMap(() => {
            // Update the image schema with new MP4 information.
            imageSchema.fileName = this.utils.changeExtension(imageSchema.fileName, IMAGE_FILETYPE.EXTENSION_MP4);
            imageSchema.filePath = this.utils.changeExtension(imageSchema.filePath, IMAGE_FILETYPE.EXTENSION_MP4);
            imageSchema.thumbFilePath = this.utils.changeExtension(imageSchema.thumbFilePath, IMAGE_FILETYPE.EXTENSION_JPEG);
            imageSchema.localFilePath = this.utils.changeExtension(imageSchema.localFilePath, IMAGE_FILETYPE.EXTENSION_MP4);
            imageSchema.mimeType = IMAGE_FILETYPE.MP4;

            const videoUrl = FILE_SYSTEM.PATH.FILE_PREFIX + convertedVideoFilePath.replace(/\\/g, "/");
            const thumbName = this.utils.changeExtension(imageSchema.fileName, IMAGE_FILETYPE.EXTENSION_JPEG);
            return this.videoUtilsService.captureVideoThumbnailFromUrl(videoUrl, thumbName).pipe(
              switchMap((thumbFile) => from(this.fileSysAccessService.saveFile(thumbFile, imageSchema.thumbFilePath))),
              catchError(() => of(false)),
              map(() => convertedVideoFilePath)
            );
          })
        );
      }),
      switchMap(() => this.getFilePaths(imageSchema, tmpFolderPath))
    );
  }

  /**
   * Processes the file conversion if needed, or simply retrieves file paths.
   * This method checks if the file is an AVI (which needs conversion to MP4) or a file that requires JPEG conversion.
   * @param imageSchema The schema containing image details.
   * @param tmpFolderPath The temporary folder path.
   * @returns An Observable of a tuple containing:
   *          [full path of local file, temporary file array, full path of thumbnail file].
   */
  private processFileConversion(imageSchema: InspectionImageSchema, tmpFolderPath: string): Observable<[string, string[], string]> {
    if (this.requiredAviConversionExtensionPattern.test(imageSchema.fileName)) {
      // If the file is an AVI, convert it to MP4.
      return this.processAviConversionToMp4(imageSchema, tmpFolderPath);
    } else if (this.requiredImageConversionExtensionPattern.test(imageSchema.fileName)) {
      // Otherwise, if it matches one of the JPEG conversion extensions, process the JPEG conversion.
      return this.processJpegConversion(imageSchema, tmpFolderPath);
    } else {
      // If no conversion is required, simply return the file paths.
      return zip(
        defer(() => this.fileSysAccessService.getFileFullPath(imageSchema.localFilePath)),
        defer(() => this.inspectionImageDbService.getTmpFile(tmpFolderPath)),
        defer(() => this.fileSysAccessService.getFileFullPath(imageSchema.thumbFilePath))
      );
    }
  }

  // IM12
  /**
   * Reads an inspection image, handles conversion if necessary, and retrieves EXIF data.
   * @param inspectionId The ID of the inspection.
   * @param inspectionImageId The ID of the specific image in the inspection.
   * @param readImageOption Options specifying how the image should be read.
   * @returns An Observable of the InspectionImage.
   */
  readInspectionImage(inspectionId: string, inspectionImageId: string, readImageOption: ReadImageOption): Observable<InspectionImage> {
    let imageSchema: InspectionImageSchema;
    let tmpFolderPath: string = "";
    let tmpFiles: string[];
    let blobPath: string = "";
    const pathSeparator = this.fileSysAccessService.getSystemPathSeparator();
    return zip(this.inspectionImageDbService.get(inspectionId, inspectionImageId), this.fileSysAccessService.getSaveBlobFolderPath()).pipe(
      map((x) => {
        imageSchema = x[0];
        blobPath = x[1];
        return x;
      }),
      // Process to obtain JPEG file information.
      switchMap((x) => {
        tmpFolderPath = blobPath + this.getTmpFilePath(imageSchema.inspectionId, imageSchema.id);
        return this.processFileConversion(imageSchema, tmpFolderPath);
      }),
      switchMap((x) => {
        const fullPath: string = x[0];
        tmpFiles = x[1] as string[];
        const outputThumbnailFilePath: string = x[2];

        // If there are temporary image files in the folder, read those files.
        let delimitedFilePath: Array<string>, outputJsonFilePath: string, filePath: string;
        if (tmpFiles && tmpFiles.length > 0) {
          filePath = tmpFolderPath + pathSeparator + tmpFiles[0];
          outputJsonFilePath = tmpFolderPath + pathSeparator + imageSchema.id + ".json";
        } else {
          filePath = fullPath.replace(/\//g, pathSeparator);
          delimitedFilePath = fullPath.split(/\\|\//);
          delimitedFilePath.pop();
          outputJsonFilePath = delimitedFilePath.join(pathSeparator) + pathSeparator + imageSchema.id + ".json";
        }
        return this.offlineUtilsService
          .readExifInfo(filePath, outputJsonFilePath, imageSchema.isDataPlate, this.utils.getMimeType(filePath), outputThumbnailFilePath)
          .pipe(switchMap((exifResult) => this.addMerryMetadata(exifResult, filePath, imageSchema)));
      }),
      switchMap((exifResult: ExifWrapperResult) => {
        if (exifResult !== null) {
          if (exifResult.imageType !== MEASUREMENT_IMAGE_TYPE.VIDEO_IMAGE) {
            imageSchema.imageDescription = exifResult.imageDescription;
            imageSchema.scopeModel = exifResult.scopeModel;
            imageSchema.datetime = exifResult.datetime;
            imageSchema.imageType =
              imageSchema.imageType !== MEASUREMENT_IMAGE_TYPE.VIDEO_IMAGE ? exifResult.imageType : MEASUREMENT_IMAGE_TYPE.VIDEO_IMAGE;
            imageSchema.orientation = exifResult.orientation;
            imageSchema.width = exifResult.width;
            imageSchema.height = exifResult.height;
            imageSchema.metadata = exifResult.metadata;
            imageSchema.vegaMeasurement = exifResult.vegaMeasurement;

            if (imageSchema.scopeModel == SCOPE_MODEL.IPLEX_ONE) {
              imageSchema.imageType = MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE;
            }

            imageSchema.isMeasurementEnabled = MeasurementMetaDataService.getIsMeasurementEnabled(imageSchema);
          } else {
            imageSchema.imageType = exifResult.imageType;
          }
        }
        // Update status.
        imageSchema.status = INSPECTION_IMAGE_STATUS_CODE.COMPLETED;

        // File conversion & status update
        return this.inspectionImageDbService.put(imageSchema);
      }),
      switchMap(() => this.fileSysAccessService.deleteFolder("temp" + pathSeparator + "inspections")),
      switchMap(() => this.getInspectionImage(inspectionId, inspectionImageId))
    );
  }

  /**
   * Merry images come back from the EXIF wrapper as plain images because their capture data is in XMP: reads that XMP
   * and returns them as Brian images, with the tip adapter from the XMP and Merry's own measurable verdict from the file
   * bytes (MerryMeasurable), which MeasurementMetaDataService uses instead of the Brian rules. If the file cannot be read,
   * the wrapper result is kept and the image is stored as not measurable. The title, tags and unmatched tags mapped from
   * the Merry tags at upload (MerryUploadService) are kept, as the wrapper's metadata replaces the stored one.
   */
  private addMerryMetadata(exifResult: ExifWrapperResult, filePath: string, imageSchema: InspectionImageSchema): Observable<ExifWrapperResult> {
    if (!MerryImageMetadata.isMerryModel(exifResult?.scopeModel)) {
      return of(exifResult);
    }

    const fileUrl = FILE_SYSTEM.PATH.FILE_PREFIX + filePath.replace(/\\/g, "/");
    return from(this.exifReaderService.fetchImageBlob(fileUrl)).pipe(
      switchMap(async (blob) => {
        const tags = await this.exifReaderService.getTags(blob);
        const isMeasurable = MerryMeasurable.isMeasurable(await blob.arrayBuffer());
        const upload = imageSchema.metadata ? (JSON.parse(imageSchema.metadata) as MerryUploadMetadata) : null;
        const metadata = MerryImageMetadata.toBrianMetadata(tags, exifResult.metadata, upload, isMeasurable);
        return { ...exifResult, imageType: MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE, metadata };
      }),
      catchError((error) => {
        console.error("Failed to read the Merry capture:", error);
        return of(exifResult);
      })
    );
  }

  // IM13
  getInspectionImageMeasurementData(inspectionId: string, inspectionImageId: string): Observable<MeasurementInfo> {
    return this.inspectionImageDbService.get(inspectionId, inspectionImageId).pipe(
      mergeMap((inspectionImage) => {
        let result: MeasurementInfo = null;

        switch (inspectionImage.imageType) {
          case MEASUREMENT_IMAGE_TYPE.VEGA_IMAGE:
            const vegaMetadata: VegaImageMetadata = JSON.parse(inspectionImage.metadata) as VegaImageMetadata;
            result = new MeasurementInfo();
            if (vegaMetadata.measurement === undefined) {
              return of(result);
            }

            for (let currentIndex: number = (vegaMetadata.measurement.measurementCnt ?? 0) - 1; currentIndex >= 0; currentIndex--) {
              const measurementData: MeasurementData = new MeasurementData();
              measurementData.measurementType = Number(vegaMetadata.measurement.data[currentIndex].type);
              measurementData.points = this.getVegaPoints(
                vegaMetadata.measurement.data[currentIndex].points,
                vegaMetadata.measurement.data[currentIndex].subpixOfPoints,
                vegaMetadata.measurement.subpixResolution
              );
              measurementData.length = vegaMetadata.measurement.data[currentIndex].length;
              measurementData.area = vegaMetadata.measurement.data[currentIndex].area;
              measurementData.depth = vegaMetadata.measurement.data[currentIndex].depth;
              measurementData.objectDistance = vegaMetadata.measurement.data[currentIndex].objectDistance;

              result.measurementDataList.push(measurementData);
            }

            result.measurementCount = vegaMetadata.measurement.measurementCnt;

            break;
          case MEASUREMENT_IMAGE_TYPE.IV7_IMAGE:
            const iv7Metadata: Iv7ImageMetadata = JSON.parse(inspectionImage.metadata) as Iv7ImageMetadata;

            result = new MeasurementInfo();
            result.measurementDataList = new Array<MeasurementData>();

            if (
              iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Area ||
              iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Lines
            ) {
              const measurementData: MeasurementData = new MeasurementData();
              measurementData.measurementType = iv7Metadata.measurementMode;
              measurementData.references = null;
              measurementData.points = iv7Metadata.points.map((point) => new Vertices2Df(point.x, point.y));
              measurementData.length = iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Lines ? iv7Metadata.result : null;
              measurementData.area = iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Area ? iv7Metadata.result : null;
              measurementData.subResult = iv7Metadata.subResult;
              result.measurementDataList.push(measurementData);

              result.measurementCount = 1;
            } else {
              for (let currentIndex: number = 0; currentIndex < iv7Metadata.measurementResultCnt; currentIndex++) {
                const measurementResult: number = iv7Metadata.measurementResults[iv7Metadata.measurementResultCnt - 1 - currentIndex];
                const hasReference: boolean =
                  currentIndex === 0 &&
                  (iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.PointToLine ||
                    iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Depth);

                const measurementData: MeasurementData = new MeasurementData();
                measurementData.measurementType = iv7Metadata.measurementMode;
                measurementData.references = hasReference ? iv7Metadata.references : null;
                measurementData.points = this.getPoints(iv7Metadata.points, currentIndex);
                measurementData.length =
                  iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Distance ||
                  iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.PointToLine
                    ? measurementResult
                    : iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Lines
                      ? iv7Metadata.result
                      : null;
                measurementData.area = iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Area ? iv7Metadata.result : null;
                measurementData.depth = iv7Metadata.measurementMode === Iv7ImageMetadataDef.MeasurementType.Depth ? measurementResult : null;
                measurementData.subResult = iv7Metadata.subResult;
                result.measurementDataList.push(measurementData);
              }

              result.measurementCount = iv7Metadata.measurementResultCnt;
            }

            break;
          case MEASUREMENT_IMAGE_TYPE.SOLEIL_IMAGE:
            const soleilMetadata: SoleilImageMetadata = JSON.parse(inspectionImage.metadata) as SoleilImageMetadata;

            result = new MeasurementInfo();
            result.measurementDataList = new Array<MeasurementData>();

            if (
              soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Area ||
              soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Lines
            ) {
              const measurementData: MeasurementData = new MeasurementData();
              measurementData.measurementType = soleilMetadata.measurementMode;
              measurementData.references = null;
              measurementData.points = soleilMetadata.points.map((point) => new Vertices2Df(point.x, point.y));
              measurementData.length = soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Lines ? soleilMetadata.result : null;
              measurementData.area = soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Area ? soleilMetadata.result : null;
              measurementData.subResult = soleilMetadata.subResult;
              result.measurementDataList.push(measurementData);

              result.measurementCount = 1;
            } else {
              for (let currentIndex: number = 0; currentIndex < soleilMetadata.measurementResultCnt; currentIndex++) {
                const measurementResult: number = soleilMetadata.measurementResults[soleilMetadata.measurementResultCnt - 1 - currentIndex];
                const hasReference: boolean =
                  currentIndex === 0 &&
                  (soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.PointToLine ||
                    soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Depth);

                const measurementData: MeasurementData = new MeasurementData();
                measurementData.measurementType = soleilMetadata.measurementMode;
                measurementData.references = hasReference ? soleilMetadata.references : null;
                measurementData.points = this.getPoints(soleilMetadata.points, currentIndex);
                measurementData.length =
                  soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Distance ||
                  soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.PointToLine
                    ? measurementResult
                    : soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Lines
                      ? soleilMetadata.result
                      : null;
                measurementData.area = soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Area ? soleilMetadata.result : null;
                measurementData.depth = soleilMetadata.measurementMode === SoleilImageMetadataDef.MeasurementType.Depth ? measurementResult : null;
                measurementData.subResult = soleilMetadata.subResult;
                result.measurementDataList.push(measurementData);
              }

              result.measurementCount = soleilMetadata.measurementResultCnt;
            }

            break;
          case MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE:
            const brianMetadata: BrianImageMetadata = JSON.parse(inspectionImage.metadata) as BrianImageMetadata;

            result = new MeasurementInfo();
            for (let currentIndex: number = 0; currentIndex < brianMetadata.commonStereoMeasurement.measurementResultCount; currentIndex++) {
              const measurementData: MeasurementData = new MeasurementData();
              measurementData.measurementType = brianMetadata.stereoMeasurements[currentIndex].measurementType;
              measurementData.points = brianMetadata.stereoMeasurements[currentIndex].measurementPoints;
              result.measurementDataList.push(measurementData);
            }
            result.measurementCount = brianMetadata.commonStereoMeasurement.measurementResultCount;

            break;
        }

        return of(result);
      })
    );
  }

  getImageCountForInspections(inspectionIds: string[]): Observable<InspectionImagesCount[]> {
    const getCountForInspectionId = (inspectionId: string) =>
      this.inspectionImageDbService.getImageCountOfInspection(inspectionId).pipe(map((count) => ({ count, inspectionId })));

    return forkJoin(inspectionIds.map((inspectionId) => getCountForInspectionId(inspectionId)));
  }

  getImageCountOfInspection(inspectionId: string): Observable<InspectionImagesCount> {
    return this.inspectionImageDbService.getImageCountOfInspection(inspectionId).pipe(
      map((imageCount) => {
        const countObject: InspectionImagesCount = {
          inspectionId: inspectionId,
          count: imageCount
        };

        return countObject;
      })
    );
  }

  private getPermission(insp: InspectionImageSchema) {
    let permission = InspectionPermission.Read;

    if (this.loginUserService.isAdminRole() || this.loginUserService.loginUser.id === insp.createdBy) {
      permission += InspectionPermission.Write;
    }

    return permission;
  }

  private genInspectionImage(schema: InspectionImageSchema, blobPath: string): Observable<InspectionImage> {
    return this.fileSysAccessService.isFileExists(this.genFilePath(schema.thumbFilePath, schema.usedImageMode)).pipe(
      mergeMap((isThumbnailFileExists) => {
        const result: InspectionImage = new InspectionImage();
        result.id = schema.id;
        result.inspectionId = schema.inspectionId;
        result.subjectLocationId = schema.subjectLocationId;
        result.mimeType = schema.mimeType;
        result.datetime = schema.datetime; // 画像の撮影日
        result.fileName = schema.fileName;
        result.filePath =
          FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.localFilePath, schema.usedImageMode) + this.genCacheParam();
        if (isThumbnailFileExists) {
          result.thumbFilePath =
            FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.thumbFilePath, schema.usedImageMode) + this.genCacheParam();
        } else {
          // サムネイル処理が無かった場合を考慮。
          result.thumbFilePath =
            FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.localFilePath, schema.usedImageMode) + this.genCacheParam();
        }
        result.permission = this.getPermission(schema);
        result.metadata = this.getFormattedMetadata(schema);
        result.comment = schema.comment;
        result.isDataPlate = schema.isDataPlate;
        result.usedImageMode = schema.usedImageMode;
        result.hasModifiedImage = schema.modifiedItems !== null;
        result.hasMeasurementImage = schema.measurementItems !== null;
        result.status = schema.status;
        result.schemaVersion = schema.schemaVersion;
        result.orientation = schema.usedImageMode === 0 ? schema.orientation : 1;
        result.sortOrder = schema.sortOrder;
        result.width = schema.width;
        result.height = schema.height;
        result.originalWidth = schema.width;
        result.originalHeight = schema.height;
        result.modifiedWidth = schema.modifiedWidth;
        result.modifiedHeight = schema.modifiedHeight;
        result.measurementWidth = schema.measurementWidth;
        result.measurementHeight = schema.measurementHeight;
        result.isMeasurementEnabled = schema.isMeasurementEnabled;
        result.imageType = schema.imageType;
        result.scopeModel = schema.scopeModel;

        if (schema.audioFilePath) {
          result.audioFilePath = FILE_SYSTEM.PATH.FILE_PREFIX + blobPath + this.genFilePath(schema.audioFilePath, 0) + this.genCacheParam();
        }

        if (schema?.bookMarkOption) {
          result.bookMarkOption = JSON.parse(schema.bookMarkOption);
        }

        return of(result);
      })
    );
  }

  private genInspectionImageSchema(inspectionId: string, file: File) {
    const schema = new InspectionImageSchema();
    const tempId = GUID.generateUUID();
    const date = new FormatDate();

    schema.id = tempId;
    schema.OfflineId = tempId;
    schema.inspectionId = inspectionId;
    schema.mimeType = file.type;
    schema.originalMimeType = file.type;
    schema.isOfflineData = true;
    schema.status = INSPECTION_IMAGE_STATUS_CODE.UPLOADING;
    schema.modifiedItems = null;
    schema.measurementItems = null;
    schema.schemaVersion = "1";
    schema.isDeleted = false;
    schema.isValid = true;
    schema.imageDescription = null;
    schema.rowVersion = null;
    schema.imageType = null;
    schema.datetime = null;
    schema.scopeModel = null;
    schema.vegaMeasurement = null;
    schema.orientation = null;
    schema.width = null;
    schema.height = null;
    schema.isDataPlate = false;
    schema.createdBy = this.user.id;
    schema.lastUpdatedBy = this.user.id;
    schema.createDate = date.toString();
    schema.lastUpdateDate = date.toString();
    schema.usedImageMode = USE_IMAGE_TYPE.ORIGINAL;
    schema.tenantId = this.tenantId;
    schema.manageUserId = this.userId;
    schema.isMeasurementEnabled = null;

    return schema;
  }

  private orderSort(img1: InspectionImageListItem, img2: InspectionImageListItem) {
    const dateTime1 = img1.datetime !== null ? new Date(img1.datetime) : null;
    const dateTime2 = img2.datetime !== null ? new Date(img2.datetime) : null;

    if (img1.sortOrder === null && img2.sortOrder === null) {
      return 0;
    }
    if (img1.sortOrder === null && img2.sortOrder !== null) {
      return 1;
    }
    if (img1.sortOrder !== null && img2.sortOrder === null) {
      return -1;
    }
    if (img1.sortOrder !== img2.sortOrder) {
      return img1.sortOrder - img2.sortOrder;
    }
    if (dateTime1 === dateTime2) {
      return 0;
    }
    if (dateTime1 === null && dateTime2 !== null) {
      return 1;
    }
    if (dateTime1 !== null && dateTime2 === null) {
      return -1;
    }
    return dateTime1 > dateTime2 ? 1 : -1;
  }

  private genInspectionStatusSchema(inspection: InspectionSchema) {
    const schema = new InspectionSchemaStatusUpdate();
    const date = new FormatDate();
    schema.id = inspection.id;
    schema.status = inspection.status === INSPECTION_STATUS_CODE.NOTINSPECTED ? INSPECTION_STATUS_CODE.EDITING : inspection.status;
    schema.lastUpdatedBy = inspection.lastUpdatedBy;
    schema.lastUpdateDate = date.toString();

    return schema;
  }

  private inspectionImageUpsert(imageSchema: InspectionImageSchema, file: File, isTempUpload = false) {
    const schema = imageSchema;
    if (isTempUpload) {
      const tmpFileName = this.utils.changeExtension(schema.fileName, IMAGE_FILETYPE.EXTENSION_JPEG);
      schema.localFilePath = this.getTmpFilePath(schema.inspectionId, schema.id, tmpFileName);
    }

    const filePath = this.genFilePath(schema.localFilePath, USE_IMAGE_TYPE.ORIGINAL);
    const imageFileManage = new UploadManagementSchema(
      schema.id,
      this.tenantId,
      UploadDataType.InspectionImageFile,
      true,
      this.userId,
      schema.inspectionId
    );
    const saveFile$ = from(this.fileSysAccessService.saveFile(file, filePath)).pipe(switchMapTo(this.uploadManagementDbService.put(imageFileManage)));

    if (!file.type.startsWith("video/") || this.requiredAviConversionExtensionPattern.test(file.name)) {
      return saveFile$;
    }

    const thumbFilePath = this.genFilePath(schema.thumbFilePath, USE_IMAGE_TYPE.ORIGINAL);
    return saveFile$.pipe(
      switchMap((result) =>
        this.videoUtilsService.captureVideoThumbnail(file).pipe(
          switchMap((thumbFile) => from(this.fileSysAccessService.saveFile(thumbFile, thumbFilePath))),
          catchError(() => this.extractVideoThumbnailWithFfmpeg(filePath, thumbFilePath)),
          catchError(() => of(null)),
          map(() => result)
        )
      )
    );
  }

  /** Chromium cannot decode every codec (Merry records HEVC): takes the thumbnail from the stored video with ffmpeg instead. */
  private extractVideoThumbnailWithFfmpeg(filePath: string, thumbFilePath: string): Observable<boolean> {
    return zip(
      defer(() => this.fileSysAccessService.getFileFullPath(filePath)),
      defer(() => this.fileSysAccessService.getFileFullPath(thumbFilePath))
    ).pipe(switchMap(([videoFullPath, thumbFullPath]) => this.offlineUtilsService.extractVideoThumbnail(videoFullPath, thumbFullPath)));
  }

  private imageAudioUpsert(imageSchema: InspectionImageSchema, file: File) {
    const savePath = imageSchema.audioFilePath.split("/").join("\\");
    const imageFileManage = new UploadManagementSchema(
      imageSchema.id,
      this.tenantId,
      UploadDataType.InspectionMeasurementImageAudioFile,
      true,
      this.userId,
      imageSchema.inspectionId
    );
    return from(this.fileSysAccessService.saveFile(file, savePath)).pipe(switchMapTo(this.uploadManagementDbService.put(imageFileManage)));
  }

  private genFilePath(filePath: string, usedImageMode: number) {
    const filePathParts = filePath.split("/");
    const fileName = filePathParts[filePathParts.length - 1];

    let _fileName = fileName;

    switch (usedImageMode) {
      case USE_IMAGE_TYPE.RETOUCHED:
        _fileName = this.MODIFIED_PREFIX + fileName;
        filePathParts.splice(filePathParts.length - 1, 0, "modified");
        break;

      case USE_IMAGE_TYPE.REMEASUREMENT:
        _fileName = this.REMEASURED_PREFIX + fileName;
        filePathParts.splice(filePathParts.length - 1, 0, "measurement");
        break;

      default:
        break;
    }

    filePathParts[filePathParts.length - 1] = _fileName;
    return filePathParts.join(this.fileSysAccessService.getSystemPathSeparator());
  }

  private genCacheParam() {
    return "?v=" + new Date().getTime();
  }

  private updateReportLocations(reportSchemas: IndividualReportSchema[], image: InspectionImageSchema, updateLocationId: string) {
    // レポートのreportLocationsも更新する
    reportSchemas
      .filter((report) => report.status !== INSPECTION_REPORT_STATUS_CODE.COMPLETED)
      .forEach((report) => {
        let locations = JSON.parse(report.reportLocations) as ReportLocation[];
        // 変更前のロケーションから削除
        locations.some((loc) => {
          if (loc.identifier === image.subjectLocationId) {
            loc.imageIds = loc.imageIds.filter((imageId) => imageId !== image.id);
            return true;
          }

          return false;
        });

        // 削除後、ロケーションの画像が0であればロケーションも削除
        const beforeLoc = locations.find((loc) => loc.identifier === image.subjectLocationId);
        if (beforeLoc && beforeLoc.imageIds.length < 1) {
          locations = locations.filter((loc) => loc.identifier !== image.subjectLocationId);
        }

        if (updateLocationId) {
          // 既に追加先のロケーションがあれば画像IDを追加
          const exist = locations.some((loc) => {
            if (loc.identifier === updateLocationId) {
              loc.imageIds.push(image.id);
              return true;
            }
            return false;
          });

          // 追加先のロケーションが無ければロケーションごと追加
          if (!exist) {
            locations.push({
              identifier: updateLocationId,
              imageIds: [image.id]
            });
          }
        }

        report.reportLocations = JSON.stringify(locations);
      });

    return reportSchemas;
  }

  private inspectionSchemaServerSync(inspectionSchema: InspectionSchema) {
    if (!this.isAppOnline) {
      return of(inspectionSchema);
    }

    const request = new InspectionForOffline(inspectionSchema);
    return this.offlineManagementService.putOfflineInspection(request.id, request).pipe(
      switchMap((inspection) => {
        const schema = new InspectionSchemaUpdate();
        schema.setInspection(this.tenantId, inspection);
        return this.inspectionDbService.updateInspection(schema);
      })
    );
  }

  /** Upload image files edited online to online blob storage. */
  private inspectionImageServerSync(imageSchema: InspectionImageSchema) {
    if (!this.isAppOnline) {
      return of(imageSchema);
    }

    const imageInfo = new InspectionImageForOffline(imageSchema);
    return this.offlineManagementService.putOfflineInspectionImage(imageInfo.inspectionId, imageInfo.id, imageInfo).pipe(
      switchMap((inspImage) => {
        const schema = new InspectionImageBaseSchema();
        schema.setInspectionImage(inspImage, this.tenantId, this.userId);
        return this.inspectionImageDbService.put(schema);
      })
    );
  }

  /** Upload online edited image meta data to online. */
  private inspectionImageDataServerSync(imageSchema: InspectionImageSchema) {
    if (!this.isAppOnline) {
      return of(imageSchema);
    }

    return from(this.offlineUtilsService.getImageObj(imageSchema))
      .pipe(
        switchMap((imageFile) =>
          this.offlineManagementService.putOfflineInspectionImageBinary(
            imageSchema.inspectionId,
            imageSchema.id,
            imageFile,
            imageSchema.fileName,
            imageSchema.subjectLocationId,
            imageSchema.filePath
          )
        )
      )
      .pipe(
        switchMap((inspectionImage) => {
          const schema = new InspectionImageBaseSchema();
          schema.setInspectionImage(inspectionImage, this.tenantId, this.userId);
          return this.inspectionImageDbService.put(schema);
        })
      );
  }

  private individualReportDeleteSync(inspectionId: string, inspectionImageId: string, reportSchemas: IndividualReportSchema[]) {
    if (!this.isAppOnline) {
      return of(reportSchemas);
    }

    return from(isOnlineStatus()).pipe(
      switchMap((isNetworkOnline) => {
        if (!isNetworkOnline) {
          return of(reportSchemas);
        }
        return this.inspectionImageOnlineService
          .deleteInspectionImage(inspectionId, inspectionImageId)
          .pipe(switchMap(() => this.inspectionImageDbService.delete(inspectionId, inspectionImageId, false)))
          .pipe(switchMap(() => this.deletedSync(inspectionId, reportSchemas)));
      })
    );
  }

  private individualReportMultiDeleteSync(inspectionId: string, inspectionImageIdDeleteList: string[], reportSchemas: IndividualReportSchema[]) {
    if (!this.isAppOnline) {
      return of(reportSchemas);
    }

    return from(isOnlineStatus()).pipe(
      switchMap((isNetworkOnline) => {
        if (!isNetworkOnline) {
          return of(reportSchemas);
        }
        return this.inspectionImageOnlineService
          .deleteInspectionImageMultiple(inspectionId, inspectionImageIdDeleteList)
          .pipe(switchMap(() => this.inspectionImageDbService.deleteList(inspectionId, inspectionImageIdDeleteList, false)))
          .pipe(switchMap(() => this.deletedSync(inspectionId, reportSchemas)));
      })
    );
  }

  private deletedSync(inspectionId: string, reportSchemas: IndividualReportSchema[]) {
    const getReportOfs = reportSchemas.map((report) => this.inspectionReportOnlineService.getInspectionReport(report.reportId));
    const getReportsOf = zip(...getReportOfs);
    const getImagesOf = this.inspectionImageOnlineService.getInspectionImages(inspectionId, null).pipe(
      switchMap((imageList) => {
        const ofs = imageList.map((img) => this.inspectionImageOnlineService.getInspectionImage(inspectionId, img.id));
        return zip(...ofs);
      })
    );

    return zip(getReportsOf, getImagesOf).pipe(
      switchMap((values) => {
        const inspectionReports = values[0];
        const inspectionImages = values[1];
        reportSchemas.forEach((schema) => {
          const target = inspectionReports.find((report) => report.id === schema.id);
          if (target) {
            schema.lastUpdateDate = target.lastUpdateDate;
            schema.reportLocations = JSON.stringify(target.reportLocations);
          }
        });

        const imageSchemas = inspectionImages.map((image) => {
          const schema = new InspectionImageSchema();
          schema.setInspectionImage(image, this.tenantId, this.userId);
          return schema;
        });

        return zip(this.individualReportDbService.putALL(reportSchemas), this.inspectionImageDbService.putList(imageSchemas));
      })
    );
  }

  /** Without a mark suffix in the name, adds the preferred mark (a Merry capture's own mark), else the default mark. */
  private genFileName(fileName: string, mark: Mark, preferredMark?: MarkItem | null) {
    let withoutMime: string = this.utils.getFilePathWithoutExtension(fileName);
    const mime: string = this.utils.getExtension(fileName);
    const marks: Array<MarkItem> = mark.items.filter((x) => x.identifier > 0);
    if (!marks.find((_) => withoutMime.endsWith("_" + _.code))) {
      // default mark の設定
      const markItem = preferredMark ?? mark.items.filter((x) => x.identifier > 0).find((x) => x.identifier === this.defaultMark);

      if (markItem) {
        const fileNameSuffix = "_" + markItem.code;
        withoutMime = withoutMime + fileNameSuffix;
      }
    }

    return withoutMime + "." + mime;
  }

  private setFilePath(imgSchema: InspectionImageSchema, inspectionTitle: string, folderName: string) {
    imgSchema.filePath = this.getFilePath(imgSchema.inspectionId, imgSchema.id, imgSchema.fileName);
    imgSchema.thumbFilePath = this.getThumbFilePath(imgSchema.inspectionId, imgSchema.id, imgSchema.fileName);
    imgSchema.localFilePath = this.getLocalFilePath(inspectionTitle, folderName, imgSchema.fileName, "");

    if (imgSchema.mimeType?.startsWith("video/")) {
      imgSchema.thumbFilePath = this.utils.changeExtension(imgSchema.thumbFilePath, IMAGE_FILETYPE.EXTENSION_JPEG);
    }

    return imgSchema;
  }

  private getFilePath(inspectionId: string, imageId: string, fileName: string): string {
    return `inspections/${inspectionId}/files/${imageId}/${fileName}`;
  }

  private getThumbFilePath(inspectionId: string, imageId: string, fileName: string, suffix: string = ""): string {
    const result: Array<string> = new Array<string>("inspections", inspectionId, "thumbs", imageId);
    if (suffix) {
      result.push(suffix.slice(0, -1));
    }
    result.push(suffix + fileName);

    return result.join("/");
  }

  private getLocalFilePath(inspectionTitle: string, folderName: string, fileName: string, suffix: string): string {
    if (suffix) {
      return `inspections/${inspectionTitle}/files/${folderName}/${suffix.slice(0, -1)}/${suffix + fileName}`;
    }
    return `inspections/${inspectionTitle}/files/${folderName}/${suffix + fileName}`;
  }

  private getTmpFilePath(inspectionId: string, imageId: string, fileName: string = null): string {
    return fileName ? `temp\\inspections\\${inspectionId}\\files\\${imageId}\\${fileName}` : `temp\\inspections\\${inspectionId}\\files\\${imageId}`;
  }

  private setMetadata(
    imgSchema: InspectionImageSchema,
    mark: Mark,
    fileName: string,
    isMviqImage = false,
    mviqTagFlagIsAvailable = false,
    merry: MerryUploadAttributes | null = null
  ) {
    const metadata = new Metadata();
    let setMark = this.defaultMark;
    mark.items.some((item) => {
      const suffix = "_" + item.code;
      const tempFileName = imgSchema.fileName.split(".")[0];
      if (tempFileName.endsWith(suffix)) {
        setMark = item.identifier;
        return true;
      }
      return false;
    });
    metadata.mark = setMark;
    metadata.title = merry?.title ?? null;

    const fileNameWithoutExtension = this.utils.getFileNameWithoutExtension(fileName);
    const isAddMviqTagFlag = isMviqImage && mviqTagFlagIsAvailable && this.mviqUtilsService.hasMviqFlagSuffix(fileNameWithoutExtension);
    const merryTags = merry?.tags.length ? merry.tags : null;
    metadata.tags = isAddMviqTagFlag ? [TAGS_FLAG_VALUE] : merryTags;
    if (merry?.unmatchedTags.length) {
      metadata.unmatchedTags = merry.unmatchedTags;
    }
    imgSchema.metadata = JSON.stringify(metadata);

    return imgSchema;
  }

  private async getByFileNameAndLocation(inspectionId: string, imageId: string, fileName: string, locationId: string) {
    return await this.inspectionImageDbService.getByFileNameAndLocation(inspectionId, imageId, fileName, locationId).toPromise();
  }

  /**
   * 拡張子から MimeType を取得する。
   *
   * @param inspectionImageSchema InspectionImageSchema。
   * @returns MimeType。変換不要な場合や判定できない場合は、 inspectionImageSchema.mimeType を返す。
   */
  private getMimeType(inspectionImageSchema: InspectionImageSchema): string {
    let result: string = this.utils.getMimeType(inspectionImageSchema.fileName);

    if (result == "") {
      result = inspectionImageSchema.mimeType;
    }

    return result;
  }

  /**
   * IdentifierからLocationFileName({{ファイル番号}} + "_" + {{Identifier}})を取得
   * @param locations
   * @param locationIdentifier
   * @returns LocationFileName({{ファイル番号}} + "_" + {{Identifier}})
   */
  private getLocationFolder(locations: SubjectLocation[], locationIdentifier: string): string {
    for (const location of locations) {
      if (location.identifier === locationIdentifier) {
        return location.folderName;
      } else if (location.children.length > 0) {
        const childResult = this.getLocationFolder(location.children, locationIdentifier);
        if (childResult !== "Unallocated") {
          return childResult;
        }
      }
    }
    return "Unallocated";
  }

  /**
   * 整形した Metadata を取得する。
   *
   * 参考 : <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/Api-RviDataManagement?path=/src/Api/Lib/DbModels/InspectionImage.cs&version=GBdev&line=66&lineEnd=67&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents>
   *
   * @param inspectionImage InspectionImageSchema。
   * @returns Metadata。
   */
  private getFormattedMetadata(inspectionImage: InspectionImageSchema): Metadata {
    let result: Metadata = null;

    switch (inspectionImage.imageType) {
      case MEASUREMENT_IMAGE_TYPE.VEGA_IMAGE:
        const vegaMetadata: VegaImageMetadata = JSON.parse(inspectionImage.metadata) as VegaImageMetadata;
        result = new Metadata();
        result.mark = vegaMetadata.mark;
        result.title = vegaMetadata.title;
        result.tags = vegaMetadata.tags;
        result.setNumber = vegaMetadata.opticalAdapter?.adapterSetNo;
        result.adapterName = vegaMetadata.opticalAdapter?.name;
        break;
      case MEASUREMENT_IMAGE_TYPE.IV7_IMAGE:
        const iv7Metadata: Iv7ImageMetadata = JSON.parse(inspectionImage.metadata) as Iv7ImageMetadata;
        result = new Metadata();
        result.mark = iv7Metadata.mark;
        result.title = iv7Metadata.title;
        result.tags = iv7Metadata.tags;
        result.setNumber = iv7Metadata.setNumber;
        result.adapterName = iv7Metadata.adapterName;
        break;
      case MEASUREMENT_IMAGE_TYPE.SOLEIL_IMAGE:
        const soleilMetadata: SoleilImageMetadata = JSON.parse(inspectionImage.metadata) as SoleilImageMetadata;
        result = new Metadata();
        result.mark = soleilMetadata.mark;
        result.title = soleilMetadata.title;
        result.tags = soleilMetadata.tags;
        result.setNumber = soleilMetadata.setNumber;
        result.adapterName = soleilMetadata.adapterName;
        break;
      case MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE:
        const brianMetadata: BrianImageMetadata = JSON.parse(inspectionImage.metadata) as BrianImageMetadata;
        result = new Metadata();
        result.mark = brianMetadata.mark;
        result.title = brianMetadata.title;
        result.tags = brianMetadata.tags;
        result.setNumber = brianMetadata.opticalAdapter?.adapterSetNo;
        result.adapterName = brianMetadata.opticalAdapter?.name;
        break;
      case MEASUREMENT_IMAGE_TYPE.THESEUS_IMAGE:
        const theseusMetadata: TheseusImageMetadata = JSON.parse(inspectionImage.metadata) as TheseusImageMetadata;
        result = new Metadata();
        result.mark = theseusMetadata.mark;
        result.title = theseusMetadata.title;
        result.tags = theseusMetadata.tags;
        result.setNumber = theseusMetadata.opticalAdapter?.adapterSetNo;
        result.adapterName = theseusMetadata.opticalAdapter?.name;
        break;
      case MEASUREMENT_IMAGE_TYPE.ANYIMAGE_IMAGE:
        const anyMetadata: AnyImageMetadata = JSON.parse(inspectionImage.metadata) as AnyImageMetadata;
        result = new Metadata();
        result.mark = anyMetadata.mark;
        result.title = anyMetadata.title;
        result.tags = anyMetadata.tags;
        break;
      case MEASUREMENT_IMAGE_TYPE.VIDEO_IMAGE:
        const videoMetadata: VideoMetadata = JSON.parse(inspectionImage.metadata) as VideoMetadata;
        result = new Metadata();
        result.mark = videoMetadata.mark;
        result.title = videoMetadata.title;
        result.tags = videoMetadata.tags;
        break;
      case MEASUREMENT_IMAGE_TYPE.NONE_IMAGE:
        result = new Metadata();
        break;
      default:
        // 2.0.27 以前のオフラインモードで作成されたデータを考慮して、 imageType が null の場合の対応を追加。
        const metadata: Metadata = JSON.parse(inspectionImage.metadata) as Metadata;
        result = new Metadata();
        result.mark = metadata?.mark;
        result.title = metadata?.title;
        result.tags = metadata?.tags;
        break;
    }
    // Merry captures only (MerryUploadService); the typed metadata classes above do not carry them.
    result.unmatchedTags = MerryImageMetadata.storedUnmatchedTags(inspectionImage.metadata);

    return result;
  }

  /**
   * マーク、タイトル、タグを編集した Metadata を取得する。
   *
   * 参考(InspectionImageManagementService.SetMarkAndTitleAndTagsToMetadata) : <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/Api-RviDataManagement?path=/src/Api/Lib/Services/InspectionImageManagementService.cs&version=GBdev&line=1970&lineEnd=1970&lineStartColumn=24&lineEndColumn=56&lineStyle=plain&_a=contents>
   *
   * @param inspectionImage 対象の検査画像。
   * @param mark マーク。
   * @param title タイトル。
   * @param tags タグ。
   * @returns 編集後、シリアライズ化した Metadata。
   */
  private getMetadataChangedMarkTitleAndTags(inspectionImage: InspectionImageSchema, mark: number, title: string, tags: Array<string>): string {
    let result: string = null;

    switch (inspectionImage.imageType) {
      case MEASUREMENT_IMAGE_TYPE.VEGA_IMAGE:
        const vegaMetadata: VegaImageMetadata = JSON.parse(inspectionImage.metadata) as VegaImageMetadata;
        vegaMetadata.mark = mark;
        vegaMetadata.title = title;
        vegaMetadata.tags = tags;
        result = JSON.stringify(vegaMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.IV7_IMAGE:
        const iv7Metadata: Iv7ImageMetadata = JSON.parse(inspectionImage.metadata) as Iv7ImageMetadata;
        iv7Metadata.mark = mark;
        iv7Metadata.title = title;
        iv7Metadata.tags = tags;
        result = JSON.stringify(iv7Metadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.SOLEIL_IMAGE:
        const soleilMetadata: SoleilImageMetadata = JSON.parse(inspectionImage.metadata) as SoleilImageMetadata;
        soleilMetadata.mark = mark;
        soleilMetadata.title = title;
        soleilMetadata.tags = tags;
        result = JSON.stringify(soleilMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE:
        const brianMetadata: BrianImageMetadata = JSON.parse(inspectionImage.metadata) as BrianImageMetadata;
        brianMetadata.mark = mark;
        brianMetadata.title = title;
        brianMetadata.tags = tags;
        result = JSON.stringify(brianMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.THESEUS_IMAGE:
        const theseusMetadata: TheseusImageMetadata = JSON.parse(inspectionImage.metadata) as TheseusImageMetadata;
        theseusMetadata.mark = mark;
        theseusMetadata.title = title;
        theseusMetadata.tags = tags;
        result = JSON.stringify(theseusMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.ANYIMAGE_IMAGE:
        const anyMetadata: AnyImageMetadata = JSON.parse(inspectionImage.metadata) as AnyImageMetadata;
        anyMetadata.mark = mark;
        anyMetadata.title = title;
        anyMetadata.tags = tags;
        result = JSON.stringify(anyMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.VIDEO_IMAGE:
        const videoMetadata: VideoMetadata = JSON.parse(inspectionImage.metadata) as VideoMetadata;
        videoMetadata.mark = mark;
        videoMetadata.title = title;
        videoMetadata.tags = tags;
        result = JSON.stringify(videoMetadata);
        break;
      default:
        // 2.0.27 以前のオフラインモードで作成されたデータを考慮して、 imageType が null の場合の対応を追加。
        const metadata: Metadata = JSON.parse(inspectionImage.metadata) as Metadata;
        metadata.mark = mark;
        metadata.title = title;
        metadata.tags = tags;
        result = JSON.stringify(metadata);
        break;
    }

    return result;
  }

  /**
   * Point の配列を取得する。
   *
   * 参考(InspectionImageManagementService.SetPoint) : <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/Api-RviDataManagement?path=/src/Api/Lib/Services/InspectionImageManagementService.cs&version=GBdev&line=377&lineEnd=393&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents>
   *
   * @param points
   * @param subpixOfPoints
   * @param subpixResolution
   * @returns
   */
  private getVegaPoints(points: Array<Vertices2Df>, subpixOfPoints: Array<Vertices2Df>, subpixResolution: Vertices2Df): Array<Vertices2Df> {
    const result: Array<Vertices2Df> = new Array<Vertices2Df>();

    for (let currentIndex: number = 0; currentIndex < 30; currentIndex++) {
      const point: Vertices2Df = new Vertices2Df(
        points[currentIndex].x + subpixOfPoints[currentIndex].x / subpixResolution.x,
        points[currentIndex].y + subpixOfPoints[currentIndex].y / subpixResolution.y
      );
      result.push(point);
    }

    return result;
  }

  /**
   * Point の配列を取得する。
   *
   * 参考(InspectionImageManagementService.SetPoint) : <https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_git/Api-RviDataManagement?path=/src/Api/Lib/Services/InspectionImageManagementService.cs&version=GBdev&line=394&lineEnd=403&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents>
   *
   * @param points
   * @param index
   * @returns
   */
  private getPoints(points: Array<Vertices2Df>, index: number): Array<Vertices2Df> {
    const result: Array<Vertices2Df> = new Array<Vertices2Df>();

    result.push(new Vertices2Df(points[index * 2].x, points[index * 2].y));
    result.push(new Vertices2Df(points[index * 2 + 1].x, points[index * 2 + 1].y));

    return result;
  }

  // 片方のSchemaが画像、もう片方のSchemaが動画ならTrue
  private isImageAndVideo(existingSchema: InspectionImageSchema, uploadSchema: InspectionImageSchema): boolean {
    const existingIsImage = this.isImage(existingSchema.mimeType);
    const existingIsVideo = this.isVideo(existingSchema.mimeType);
    const uploadIsImage = this.isImage(uploadSchema.mimeType);
    const uploadIsVideo = this.isVideo(uploadSchema.mimeType);

    return (existingIsImage && uploadIsVideo) || (existingIsVideo && uploadIsImage);
  }

  private isImage(mimeType: string): boolean {
    const imageMimeTypes = [
      IMAGE_FILETYPE.JPEG,
      IMAGE_FILETYPE.PNG,
      IMAGE_FILETYPE.BMP,
      IMAGE_FILETYPE.TIF,
      IMAGE_FILETYPE.TIFF,
      IMAGE_FILETYPE.EXTENSION_HEIF,
      IMAGE_FILETYPE.EXTENSION_HEIC
    ];
    return imageMimeTypes.includes(mimeType);
  }

  private isVideo(mimeType: string): boolean {
    const videoMimeTypes = [IMAGE_FILETYPE.MP4];
    return videoMimeTypes.includes(mimeType);
  }
}
